import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { TableVirtuoso } from 'react-virtuoso';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { FiPlay, FiSquare, FiRefreshCw, FiTrash2, FiUsers, FiCheckCircle, FiXCircle, FiClock, FiPlus, FiRadio, FiZap, FiArrowLeft, FiCpu, FiAlertCircle, FiEdit2, FiMail, FiPaperclip, FiChevronDown, FiChevronUp, FiMessageCircle, FiDownload } from 'react-icons/fi';
import { useSector } from '../contexts/SectorContext';
import { useModules } from '../contexts/ModuleContext';
import SessionService from '../services/SessionService';
import {
  listBroadcasts,
  getBroadcast,
  createBroadcast,
  updateBroadcast,
  startBroadcast,
  startBroadcastWorkerTest,
  cancelBroadcast,
  rerunBroadcast,
  deleteBroadcast,
  validateBroadcastLids,
  Broadcast,
  BroadcastSummary,
  BroadcastRecipient,
  BroadcastExecution,
  UpdateBroadcastRequest,
  isBroadcastAutoPaused,
  isBroadcastEditLocked,
  type LIDValidationReport,
} from '../services/BroadcastService';
import { getEmailContactsPaged, importEmailContactsFromFile } from '../services/EmailContactService';
import { listEmailBroadcasts, createEmailBroadcast, startEmailBroadcast, getEmailBroadcast, deleteEmailBroadcast, toggleBroadcastAI, toggleRecipientAI, type EmailBroadcastItem, type EmailBroadcastDetail, type CreateEmailBroadcastRequest } from '../services/EmailBroadcastService';
import { getWhatsAppGroups, getWhatsAppGroupParticipants } from '../services/SectorService';
import {
  fetchGroupParticipantsDeduped,
  getCachedGroupParticipants,
} from '../utils/groupParticipantsCache';
import {
  contactAddrKey,
  contactSelectionKey,
  mapGroupParticipantsToContactItems,
  resolveGroupParticipantsEmptyMessage,
  resolveGroupParticipantsErrorMessage,
  resolveWhatsAppGroupApiId,
  type BroadcastContactItem,
} from '../utils/broadcastContactUtils';
import { canRerunBroadcast } from '../utils/broadcastLifecycleUtils';
import { getSectorAiConfig, listUnifiedAgents, type UnifiedAiAgentDTO } from '../services/UnifiedAIAgentService';
import { optimizeText } from '../services/TextOptimizationService';
import {
  getBroadcastTableNameCell,
  getBroadcastTableNumberCell,
} from '../utils/formatters';
import { config } from '../config';
import { ScheduleCalendar } from '../components/ScheduleCalendar';
import { ScheduleTimePicker } from '../components/ScheduleTimePicker';
import { TimezoneSelect } from '../components/TimezoneSelect';

import { ConfirmModal, SharedModal } from '../components/SharedModal';
import '../components/SharedModal/ConfirmModal.css';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { VirtualizedBroadcastContactList } from '../components/broadcast/VirtualizedBroadcastContactList';
import { VirtualizedBroadcastGroupPicker } from '../components/broadcast/VirtualizedBroadcastGroupPicker';
import { ExportGroupContactsModal } from '../components/broadcast/ExportGroupContactsModal';
import SparkCampaignsPanel from '../components/broadcast/SparkCampaignsPanel';
import BroadcastMessageVariantEditor from '../components/BroadcastMessageVariantEditor';
import {
  BroadcastMessageVariantUi,
  applyAiOptimizeToFirstTextVariant,
  broadcastVariantFromApiMessage,
  createEmptyBroadcastVariant,
  isBroadcastVariantValid,
  prepareBroadcastVariantsForApi,
} from '../utils/broadcastMessageVariantUtils';
import './BroadcastPage.css';

dayjs.extend(timezone);
dayjs.extend(utc);

/** Readable delivery error for email broadcast table (collapse whitespace, trim length). */
function humanizeEmailDeliveryError(raw: string | null | undefined): string {
  if (raw == null || !String(raw).trim()) return '';
  let s = String(raw).replace(/\r\n/g, '\n').trim();
  s = s.replace(/\n{3,}/g, '\n\n').replace(/[ \t]+/g, ' ');
  if (s.length > 500) s = `${s.slice(0, 497)}…`;
  return s;
}

function emailRecipientResultLabel(status: string): string {
  const s = (status || '').toLowerCase();
  if (s === 'sent') return 'Enviado';
  if (s === 'failed') return 'Falhou';
  if (s === 'pending') return 'Pendente';
  return status || '—';
}

const BROADCAST_STATUS_LABELS: Record<string, string> = {
  pending: 'Pendente',
  running: 'Em andamento',
  completed: 'Concluída',
  cancelled: 'Cancelada',
  canceled: 'Cancelada',
  paused: 'Pausada',
  paused_daily: 'Pausada (limite diário)',
  failed: 'Falhou',
  sent: 'Enviado',
  draft: 'Rascunho',
  skipped: 'Ignorado',
};

/** Rótulo em português para o status vindo da API (o valor bruto continua na classe CSS). */
function broadcastStatusLabel(status: string | null | undefined): string {
  const s = (status || '').toLowerCase();
  return BROADCAST_STATUS_LABELS[s] ?? (status || '—');
}

function emailRecipientStatusClass(status: string): string {
  const s = (status || '').toLowerCase();
  if (s === 'sent') return 'sent';
  if (s === 'failed') return 'failed';
  if (s === 'pending') return 'pending';
  return s;
}

interface WhatsAppGroup {
  id: number;
  jid: string;
  name: string;
  participantCount: number;
  participant_count?: number;
  whatsappGroupId?: number;
  contactId?: number;
}

type ContactItem = BroadcastContactItem;

function resolveGroupParticipantsApiId(
  groups: WhatsAppGroup[],
  listGroupId: number
): number | null {
  const group = groups.find((g) => g.id === listGroupId);
  return resolveWhatsAppGroupApiId(group ?? null, listGroupId);
}

const BROADCAST_SECRET_RC_WINDOW_MS = 2500;
const BROADCAST_SECRET_RC_COUNT = 5;

/**
 * User chose Contacts → import file + campaign. Audience is that list only; do not show group/recipient pickers,
 * independent of whether numbers already existed in CRM.
 */
function broadcastRecipientsFromImportedList(b: Broadcast): boolean {
  if (b.audience_from_import === true) return true;
  if (b.source_import_list_id != null && b.source_import_list_id > 0) return true;
  return false;
}

function recipientSnapshotToSelectionKeys(recipients: BroadcastRecipient[]): Set<string> {
  const pre = new Set<string>();
  for (const r of recipients) {
    if (r.contact_id > 0) pre.add(`id:${r.contact_id}`);
    else {
      const ak = contactAddrKey(r.number || '');
      if (ak) pre.add(`addr:${ak}`);
    }
  }
  return pre;
}

function selectionKeysForGroupContacts(contacts: ContactItem[], pending: Set<string>): Set<string> {
  if (pending.size === 0) {
    return new Set(contacts.map((c) => contactSelectionKey(c)));
  }
  const matched = contacts.filter((c) => pending.has(contactSelectionKey(c)));
  if (matched.length > 0) {
    return new Set(matched.map((c) => contactSelectionKey(c)));
  }
  return new Set(contacts.map((c) => contactSelectionKey(c)));
}

const PAGE_SIZE = 10;

function formatLidValidationLog(r: LIDValidationReport): string {
  const lines: string[] = [];
  lines.push(`Broadcast #${r.broadcast_id} · sector ${r.sector_id} · scope=${r.scope}`);
  lines.push(
    `Client ready: ${r.client_ready} · elapsed ${typeof r.seconds_elapsed === 'number' ? r.seconds_elapsed.toFixed(1) : r.seconds_elapsed}s`
  );
  lines.push(
    `Summary: total=${r.total} ok=${r.ok_count} fail=${r.fail_count} skipped=${r.skipped_count} pending_in_db=${r.pending_in_db}`
  );
  if (r.error) lines.push(`ERROR: ${r.error}`);
  lines.push('');
  for (const e of r.entries || []) {
    lines.push(`--- row #${e.recipient_row_id} contact_id=${e.contact_id} ${e.contact_name || ''} kind=${e.kind} ok=${e.ok}`);
    lines.push(`    input=${e.input}`);
    lines.push(`    parsed_jid=${e.parsed_jid}`);
    const wa =
      e.whatsapp_registered === undefined || e.whatsapp_registered === null
        ? 'n/a'
        : String(e.whatsapp_registered);
    lines.push(
      `    jid_ok=${e.jid_ok ?? 'n/a'} whatsapp=${wa} lid_in_store=${e.lid_in_store ?? 'n/a'} migration=${e.lid_migration_on ?? 'n/a'} hint=${e.send_hint ?? ''}`
    );
    if (e.resolved_lid) lines.push(`    resolved_lid=${e.resolved_lid}`);
    if (e.input_shape || e.address_type || e.primary_send_jid) {
      lines.push(
        `    shape=${e.input_shape ?? ''} address=${e.address_type ?? ''} primary=${e.primary_send_jid ?? ''} preferred=${e.preferred_send_jid ?? ''}`
      );
    }
    if (e.contact_origin) {
      lines.push(`    contact_origin=${e.contact_origin}${e.contact_origin_detail ? ' · ' + e.contact_origin_detail : ''}`);
    }
    for (const s of e.steps || []) lines.push(`    | ${s}`);
  }
  return lines.join('\n');
}

const BroadcastPage: React.FC = () => {
  const { selectedSectorId, isOfficial } = useSector();
  const { hasModule } = useModules();
  const navigate = useNavigate();
  const [broadcasts, setBroadcasts] = useState<Broadcast[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showExportGroupsModal, setShowExportGroupsModal] = useState(false);
  const [selectedBroadcast, setSelectedBroadcast] = useState<BroadcastSummary | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const pollRef = useRef<NodeJS.Timeout | null>(null);
  const groupParticipantsAbortRef = useRef<AbortController | null>(null);
  const groupParticipantsRequestIdRef = useRef(0);
  const editGroupParticipantsRequestIdRef = useRef(0);
  const editPendingRecipientKeysRef = useRef<Set<string>>(new Set());

  const [statusFilter, setStatusFilter] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(0);
  const [totalBroadcasts, setTotalBroadcasts] = useState(0);

  const [formName, setFormName] = useState('');
  const [formVariants, setFormVariants] = useState<BroadcastMessageVariantUi[]>([createEmptyBroadcastVariant()]);
  const PREVIEW_EXAMPLE_NAME = 'João';
  const [formSelectedContacts, setFormSelectedContacts] = useState<Set<string>>(new Set());
  const [formContactSearch, setFormContactSearch] = useState('');
  const [contactsLoading, setContactsLoading] = useState(false);
  const [groups, setGroups] = useState<WhatsAppGroup[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
  const [groupContacts, setGroupContacts] = useState<ContactItem[]>([]);
  const [creating, setCreating] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const startShortcutArmRef = useRef<{ broadcastId: number | null; rcCount: number; lastTs: number }>({
    broadcastId: null,
    rcCount: 0,
    lastTs: 0,
  });
  const [startShortcutArmedBroadcastId, setStartShortcutArmedBroadcastId] = useState<number | null>(null);
  const [broadcastAIActive, setBroadcastAIActive] = useState<boolean | null>(null);
  const [formAiAgentId, setFormAiAgentId] = useState<number | null>(null);
  const [formAiReplyEnabled, setFormAiReplyEnabled] = useState(true);
  const [aiCampaignAgents, setAiCampaignAgents] = useState<UnifiedAiAgentDTO[]>([]);
  const [aiOptimizing, setAiOptimizing] = useState<string | null>(null);

  const [recurrenceType, setRecurrenceType] = useState<number>(1);
  const [recurrenceInterval, setRecurrenceInterval] = useState<number>(1);
  const [recurrenceEndDate, setRecurrenceEndDate] = useState<string>('');
  const [recurrenceWeekdays, setRecurrenceWeekdays] = useState<number[]>([]);
  const [recurrenceMonths, setRecurrenceMonths] = useState<number[]>([]); // 1-12 = Jan-Dec for Monthly
  const [excludeWeekends, setExcludeWeekends] = useState(false);
  const [excludeHolidays, setExcludeHolidays] = useState(false);
  const [scheduledTime, setScheduledTime] = useState<string>('09:00');
  const [timezone, setTimezone] = useState<string>('');

  type ConfirmActionType = 'start' | 'rerun' | 'cancel' | 'delete' | null;
  const [confirmAction, setConfirmAction] = useState<ConfirmActionType>(null);
  const [confirmBroadcastId, setConfirmBroadcastId] = useState<number | null>(null);
  const [confirmBroadcastStatus, setConfirmBroadcastStatus] = useState<string | null>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [confirmActionError, setConfirmActionError] = useState<string | null>(null);
  const [lidValidationOpen, setLidValidationOpen] = useState(false);
  const [lidValidationLoading, setLidValidationLoading] = useState(false);
  const [lidValidationReport, setLidValidationReport] = useState<LIDValidationReport | null>(null);
  const [lidValidationBroadcastId, setLidValidationBroadcastId] = useState<number | null>(null);
  const [lidValidationStartError, setLidValidationStartError] = useState<string | null>(null);



  const [showEditModal, setShowEditModal] = useState(false);
  const [showEditBlockedRunningModal, setShowEditBlockedRunningModal] = useState(false);
  const [editingBroadcastId, setEditingBroadcastId] = useState<number | null>(null);
  const [editingBroadcast, setEditingBroadcast] = useState<BroadcastSummary | null>(null);
  const [editName, setEditName] = useState('');
  const [editVariants, setEditVariants] = useState<BroadcastMessageVariantUi[]>([createEmptyBroadcastVariant()]);
  const [editRecurrenceType, setEditRecurrenceType] = useState<number>(1);
  const [editRecurrenceInterval, setEditRecurrenceInterval] = useState<number>(1);
  const [editRecurrenceEndDate, setEditRecurrenceEndDate] = useState<string>('');
  const [editRecurrenceWeekdays, setEditRecurrenceWeekdays] = useState<number[]>([]);
  const [editRecurrenceMonths, setEditRecurrenceMonths] = useState<number[]>([]); // 1-12 = Jan-Dec
  const [editExcludeWeekends, setEditExcludeWeekends] = useState(false);
  const [editExcludeHolidays, setEditExcludeHolidays] = useState(false);
  const [editScheduledTime, setEditScheduledTime] = useState<string>('09:00');
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  /* Edit: groups and recipients (like create) */
  const [editSelectedGroupId, setEditSelectedGroupId] = useState<number | null>(null);
  const [editGroups, setEditGroups] = useState<WhatsAppGroup[]>([]);
  const [editGroupContacts, setEditGroupContacts] = useState<ContactItem[]>([]);
  const [editSelectedContacts, setEditSelectedContacts] = useState<Set<string>>(new Set());
  const [editContactSearch, setEditContactSearch] = useState('');
  const [editContactsLoading, setEditContactsLoading] = useState(false);
  const [editAiAgentId, setEditAiAgentId] = useState<number | null>(null);
  const [editAiReplyEnabled, setEditAiReplyEnabled] = useState(true);
  const [editAiCampaignAgents, setEditAiCampaignAgents] = useState<UnifiedAiAgentDTO[]>([]);

  /* Main tab: Campaigns | Email Campaigns */
  const [activeTab, setActiveTab] = useState<'campanhas' | 'email'>('campanhas');

  /* Email campaigns state */
  const [emailBroadcasts, setEmailBroadcasts] = useState<EmailBroadcastItem[]>([]);
  const [emailBroadcastsLoading, setEmailBroadcastsLoading] = useState(false);
  const [showCreateEmailModal, setShowCreateEmailModal] = useState(false);
  const [emailFormName, setEmailFormName] = useState('');
  const [emailFormSubject, setEmailFormSubject] = useState('');
  const [emailFormBody, setEmailFormBody] = useState('');
  const [emailFormSelectedContacts, setEmailFormSelectedContacts] = useState<Set<number>>(new Set());
  const [emailFormCc, setEmailFormCc] = useState('');
  const [emailFormBcc, setEmailFormBcc] = useState('');
  const [emailFormReplyTo, setEmailFormReplyTo] = useState('');
  const [emailFormAttachments, setEmailFormAttachments] = useState<Array<{ id: string; file: File }>>([]);
  const [showCcBccSection, setShowCcBccSection] = useState(false);
  const [emailContacts, setEmailContacts] = useState<Array<{ id: number; name: string; email: string }>>([]);
  const [emailContactsLoading, setEmailContactsLoading] = useState(false);
  const [emailCreating, setEmailCreating] = useState(false);
  const [emailExcelImporting, setEmailExcelImporting] = useState(false);
  const [emailExcelError, setEmailExcelError] = useState<string | null>(null);
  const [emailFormAiAgentId, setEmailFormAiAgentId] = useState<number | null>(null);
  const [selectedEmailBroadcast, setSelectedEmailBroadcast] = useState<EmailBroadcastDetail | null>(null);
  const [emailDetailLoading, setEmailDetailLoading] = useState(false);
  const emailPollRef = useRef<NodeJS.Timeout | null>(null);
  const emailListPollRef = useRef<NodeJS.Timeout | null>(null);
  const [emailBackgroundNotice, setEmailBackgroundNotice] = useState<string | null>(null);

  type EmailConfirmActionType = 'start' | 'delete' | null;
  const [emailConfirmAction, setEmailConfirmAction] = useState<EmailConfirmActionType>(null);
  const [emailConfirmBroadcastId, setEmailConfirmBroadcastId] = useState<number | null>(null);
  const [emailConfirmLoading, setEmailConfirmLoading] = useState(false);

  /* Schedule Time/Date picker modals (create) */
  const [showScheduleTimePicker, setShowScheduleTimePicker] = useState(false);
  const [showScheduleDatePicker, setShowScheduleDatePicker] = useState(false);
  /* Schedule Time/Date picker modals (edit) */
  const [showEditTimePicker, setShowEditTimePicker] = useState(false);
  const [showEditDatePicker, setShowEditDatePicker] = useState(false);

  const getSectorId = (): number | null => {
    return selectedSectorId || SessionService.getSectorId() || null;
  };
  const sectorId = getSectorId();

  const displayedFormContacts = useMemo(() => {
    if (!selectedGroupId) return [];
    const q = formContactSearch.trim();
    if (!q) return groupContacts;
    const search = q.toLowerCase();
    return groupContacts.filter(
      (c) => c.name.toLowerCase().includes(search) || c.number.includes(search)
    );
  }, [selectedGroupId, groupContacts, formContactSearch]);

  const displayedFormSelectedCount = useMemo(() => {
    let count = 0;
    for (const c of displayedFormContacts) {
      if (formSelectedContacts.has(contactSelectionKey(c))) count++;
    }
    return count;
  }, [displayedFormContacts, formSelectedContacts]);

  const displayedEditContacts = useMemo(() => {
    if (!editSelectedGroupId) return [];
    const q = editContactSearch.trim();
    if (!q) return editGroupContacts;
    const search = q.toLowerCase();
    return editGroupContacts.filter(
      (c) => c.name.toLowerCase().includes(search) || c.number.includes(search)
    );
  }, [editSelectedGroupId, editGroupContacts, editContactSearch]);

  const displayedEditSelectedCount = useMemo(() => {
    let count = 0;
    for (const c of displayedEditContacts) {
      if (editSelectedContacts.has(contactSelectionKey(c))) count++;
    }
    return count;
  }, [displayedEditContacts, editSelectedContacts]);

  const loadBroadcasts = useCallback(async (page?: number, status?: string) => {
    if (!sectorId) {
      setLoading(false);
      return;
    }
    const p = page ?? currentPage;
    const s = status ?? statusFilter;
    try {
      const result = await listBroadcasts(sectorId, {
        status: s || undefined,
        limit: PAGE_SIZE,
        offset: p * PAGE_SIZE,
      });
      setBroadcasts(result.broadcasts);
      setTotalBroadcasts(result.pagination.total);
    } catch (err) {
      console.error('Error loading broadcasts:', err);
    } finally {
      setLoading(false);
    }
  }, [sectorId, currentPage, statusFilter]);

  useEffect(() => {
    if (!sectorId) {
      setLoading(false);
      setBroadcasts([]);
      return;
    }
    setLoading(true);
    loadBroadcasts();
  }, [loadBroadcasts, sectorId]);

  useEffect(() => {
    const token = SessionService.getToken();
    if (!token) return;
    fetch(`${config.API_BASE_URL}/users/profile`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.json())
      .then((json: { data?: { defaultTimezone?: string } }) => {
        const tz = json?.data?.defaultTimezone?.trim();
        if (tz) setTimezone(tz);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!detailLoading) return;
    const onEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setDetailLoading(false);
        setSelectedBroadcast(null);
      }
    };
    window.addEventListener('keydown', onEscape);
    return () => window.removeEventListener('keydown', onEscape);
  }, [detailLoading]);

  useEffect(() => {
    if (!sectorId) {
      setBroadcastAIActive(null);
      return;
    }
    const token = SessionService.getToken();
    if (!token) {
      setBroadcastAIActive(null);
      return;
    }
    setBroadcastAIActive(null);
    Promise.all([
      getSectorAiConfig(sectorId, token),
      listUnifiedAgents(sectorId, token, 'campaign').catch(() => [] as UnifiedAiAgentDTO[]),
    ])
      .then(([receptive, agents]) => {
        const hasCampaignAI = Array.isArray(agents) && agents.some((a) => a.isEnabled);
        setBroadcastAIActive(receptive?.receptiveAiEnabled === true || hasCampaignAI);
      })
      .catch(() => setBroadcastAIActive(false));
  }, [sectorId]);

  const loadEmailBroadcasts = useCallback(async () => {
    if (!sectorId) return;
    setEmailBroadcastsLoading(true);
    try {
      const result = await listEmailBroadcasts(sectorId, { page: 1, pageSize: 50 });
      setEmailBroadcasts(result.items);
    } catch (err) {
      console.error('Error loading email broadcasts:', err);
    } finally {
      setEmailBroadcastsLoading(false);
    }
  }, [sectorId]);

  useEffect(() => {
    if (activeTab === 'email' && sectorId) {
      loadEmailBroadcasts();
    }
  }, [activeTab, sectorId, loadEmailBroadcasts]);

  useEffect(() => {
    if (!sectorId) {
      setAiCampaignAgents([]);
      return;
    }
    const token = SessionService.getToken();
    if (!token) return;
    listUnifiedAgents(sectorId, token, 'campaign')
      .then((list: any) => setAiCampaignAgents(Array.isArray(list) ? list.filter((a: any) => a.isEnabled !== false) : []))
      .catch(() => setAiCampaignAgents([]));
  }, [sectorId]);

  const loadEmailContacts = useCallback(async () => {
    if (!sectorId) return;
    setEmailContactsLoading(true);
    try {
      const result = await getEmailContactsPaged(sectorId, 1, 100);
      setEmailContacts((result.items ?? []).map((c: { id: number; name: string; email: string }) => ({ id: c.id, name: c.name, email: c.email })));
    } catch (err) {
      console.error('Error loading email contacts:', err);
    } finally {
      setEmailContactsLoading(false);
    }
    const tok = SessionService.getToken();
    if (tok) {
      try {
        const agents = await listUnifiedAgents(sectorId, tok, 'campaign');
        setAiCampaignAgents(agents);
      } catch { setAiCampaignAgents([]); }
    }
  }, [sectorId]);

  const handleEmailBroadcastExcel = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f || !sectorId) return;
    setEmailExcelError(null);
    setEmailExcelImporting(true);
    try {
      const imported = await importEmailContactsFromFile(f, sectorId);
      setEmailFormSelectedContacts(new Set(imported.map((c) => c.id)));
      setEmailContacts((prev) => {
        const byId = new Map(prev.map((x) => [x.id, x]));
        for (const c of imported) {
          byId.set(c.id, { id: c.id, name: c.name, email: c.email });
        }
        return Array.from(byId.values());
      });
    } catch (err: unknown) {
      setEmailExcelError(err instanceof Error ? err.message : 'Não foi possível importar o arquivo');
    } finally {
      setEmailExcelImporting(false);
    }
  }, [sectorId]);

  useEffect(() => {
    if (selectedEmailBroadcast && (selectedEmailBroadcast.broadcast.status === 'running' || selectedEmailBroadcast.broadcast.status === 'paused_daily')) {
      emailPollRef.current = setInterval(async () => {
        try {
          const updated = await getEmailBroadcast(selectedEmailBroadcast.broadcast.id);
          if (updated) {
            setSelectedEmailBroadcast(updated);
            loadEmailBroadcasts();
          }
        } catch { /* ignore */ }
      }, 3000);
    }
    return () => {
      if (emailPollRef.current) clearInterval(emailPollRef.current);
    };
  }, [selectedEmailBroadcast?.broadcast.id, selectedEmailBroadcast?.broadcast.status, loadEmailBroadcasts]);

  useEffect(() => {
    const hasRunning = emailBroadcasts.some(b => b.status === 'running' || b.status === 'paused_daily');
    if (hasRunning && !selectedEmailBroadcast && activeTab === 'email') {
      emailListPollRef.current = setInterval(() => {
        loadEmailBroadcasts();
      }, 5000);
    }
    return () => {
      if (emailListPollRef.current) clearInterval(emailListPollRef.current);
    };
  }, [emailBroadcasts, selectedEmailBroadcast, activeTab, loadEmailBroadcasts]);

  const handleViewEmailDetail = async (id: number) => {
    setEmailDetailLoading(true);
    try {
      const detail = await getEmailBroadcast(id);
      if (detail) {
        setSelectedEmailBroadcast(detail);
        if (detail.broadcast.aiAgentId && sectorId) {
          const tok = SessionService.getToken();
          if (tok) {
            try {
              const agents = await listUnifiedAgents(sectorId, tok, 'campaign');
              setAiCampaignAgents(agents);
            } catch { /* ignore */ }
          }
        }
      }
    } catch (err) {
      console.error('Error loading email broadcast detail:', err);
    } finally {
      setEmailDetailLoading(false);
    }
  };

  const getEmailProgressPercent = (b: EmailBroadcastItem | EmailBroadcastDetail['broadcast']) => {
    if (b.totalCount === 0) return 0;
    return Math.round(((b.sentCount + b.failedCount) / b.totalCount) * 100);
  };

  const openEmailConfirm = (action: EmailConfirmActionType, id: number, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setEmailConfirmAction(action);
    setEmailConfirmBroadcastId(id);
  };

  const handleEmailConfirmAction = async () => {
    if (!emailConfirmBroadcastId || !emailConfirmAction) return;
    setEmailConfirmLoading(true);
    try {
      if (emailConfirmAction === 'start') {
        const startResult = await startEmailBroadcast(emailConfirmBroadcastId);
        const notice = startResult?.message?.trim() || 'Enviando em segundo plano. Você pode sair desta página.';
        setEmailBackgroundNotice(notice);
        window.setTimeout(() => setEmailBackgroundNotice(null), 10000);
      } else if (emailConfirmAction === 'delete') {
        await deleteEmailBroadcast(emailConfirmBroadcastId);
        if (selectedEmailBroadcast?.broadcast.id === emailConfirmBroadcastId) {
          setSelectedEmailBroadcast(null);
        }
      }
      loadEmailBroadcasts();
      if (selectedEmailBroadcast && emailConfirmAction !== 'delete') {
        const updated = await getEmailBroadcast(selectedEmailBroadcast.broadcast.id);
        if (updated) setSelectedEmailBroadcast(updated);
      }
    } catch (err) {
      console.error('Email confirm action error:', err);
    } finally {
      setEmailConfirmLoading(false);
      setEmailConfirmAction(null);
      setEmailConfirmBroadcastId(null);
    }
  };

  const handleStatusFilterChange = (newStatus: string) => {
    setStatusFilter(newStatus);
    setCurrentPage(0);
    setLoading(true);
    loadBroadcasts(0, newStatus);
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    setLoading(true);
    loadBroadcasts(newPage);
  };

  const totalPages = Math.ceil(totalBroadcasts / PAGE_SIZE);

  useEffect(() => {
    if (
      selectedBroadcast &&
      (selectedBroadcast.broadcast.status === 'running' ||
        selectedBroadcast.broadcast.status === 'pending' ||
        isBroadcastAutoPaused(selectedBroadcast.broadcast))
    ) {
      const pollBroadcastId = selectedBroadcast.broadcast.id;
      pollRef.current = setInterval(async () => {
        const updated = await getBroadcast(pollBroadcastId);
        if (updated) {
          setSelectedBroadcast(updated);
          loadBroadcasts();
        }
      }, 3000);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [selectedBroadcast?.broadcast.id, selectedBroadcast?.broadcast.status, loadBroadcasts]);

  const listPollRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    const hasRunning = broadcasts.some(
      b => b.status === 'running' || b.status === 'pending' || isBroadcastAutoPaused(b)
    );
    if (hasRunning && !selectedBroadcast) {
      listPollRef.current = setInterval(() => {
        loadBroadcasts();
      }, 5000);
    }
    return () => {
      if (listPollRef.current) clearInterval(listPollRef.current);
    };
  }, [broadcasts, selectedBroadcast, loadBroadcasts]);


  const handleOpenCreate = async () => {
    const sid = selectedSectorId || SessionService.getSectorId();
    if (!sid) return;

    setFormName('');
    setFormVariants([createEmptyBroadcastVariant()]);
    setFormSelectedContacts(new Set());
    setFormContactSearch('');
    setSelectedGroupId(null);
    setGroupContacts([]);
    setFormError(null);
    setFormAiAgentId(null);
    setFormAiReplyEnabled(true);
    setContactsLoading(true);
    setShowCreateModal(true);
    setRecurrenceType(1);
    setRecurrenceInterval(1);
    setRecurrenceEndDate('');
    setRecurrenceWeekdays([]);
    setRecurrenceMonths([]);
    setExcludeWeekends(false);
    setExcludeHolidays(false);
    setScheduledTime('09:00');

    const token = SessionService.getToken();
    if (token) {
      fetch(`${config.API_BASE_URL}/users/profile`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => res.json())
        .then((json: { data?: { defaultTimezone?: string } }) => {
          const tz = json?.data?.defaultTimezone?.trim();
          if (tz) setTimezone(tz);
        })
        .catch(() => {});
    }

    let groupsList: WhatsAppGroup[] = [];
    try {
      const groupsResponse = await getWhatsAppGroups(sid);
      groupsList = Array.isArray(groupsResponse?.data) ? groupsResponse.data : [];
      setGroups(groupsList);
    } catch (err) {
      console.error('[Broadcast] Error loading groups:', err);
      setGroups([]);
    } finally {
      setContactsLoading(false);
    }

    const tok = SessionService.getToken();
    if (tok) {
      try {
        const agents = await listUnifiedAgents(sid, tok, 'campaign');
        setAiCampaignAgents(agents);
      } catch {
        setAiCampaignAgents([]);
      }
    }
  };

  const handlePendingStartButtonContextMenu = (e: React.MouseEvent, broadcastId: number) => {
    e.preventDefault();
    e.stopPropagation();
    const now = Date.now();
    const r = startShortcutArmRef.current;
    if (r.broadcastId !== broadcastId || now - r.lastTs > BROADCAST_SECRET_RC_WINDOW_MS) {
      r.rcCount = 0;
      r.broadcastId = broadcastId;
    }
    r.lastTs = now;
    r.rcCount += 1;
    if (r.rcCount >= BROADCAST_SECRET_RC_COUNT) {
      r.rcCount = 0;
      setStartShortcutArmedBroadcastId(prev => (prev === broadcastId ? null : broadcastId));
    }
  };

  const handleGroupChange = async (groupId: number | null) => {
    groupParticipantsAbortRef.current?.abort();
    const requestId = ++groupParticipantsRequestIdRef.current;
    const ac = new AbortController();
    groupParticipantsAbortRef.current = ac;

    setSelectedGroupId(groupId);
    setFormError(null);
    setFormSelectedContacts(new Set());
    setGroupContacts([]);

    if (!groupId) {
      return;
    }

    const sid = selectedSectorId || SessionService.getSectorId();
    if (!sid) return;

    const apiGroupId = resolveGroupParticipantsApiId(groups, groupId);
    if (!apiGroupId) {
      setFormError('Grupo inválido. Não foi possível identificar o grupo no WhatsApp.');
      return;
    }

    const cached = getCachedGroupParticipants(sid, apiGroupId);
    if (cached?.data?.length) {
      const cachedContacts = mapGroupParticipantsToContactItems(cached.data);
      if (requestId === groupParticipantsRequestIdRef.current && cachedContacts.length > 0) {
        setGroupContacts(cachedContacts);
        setFormSelectedContacts(new Set(cachedContacts.map((c) => contactSelectionKey(c))));
      }
    }

    setContactsLoading(true);
    try {
      const needsRefresh = !(cached?.data?.length);
      const response = await fetchGroupParticipantsDeduped(
        sid,
        apiGroupId,
        (refresh, signal) =>
          getWhatsAppGroupParticipants(sid, apiGroupId, { refresh, signal: signal ?? ac.signal }),
        { signal: ac.signal, refresh: needsRefresh }
      );
      if (requestId !== groupParticipantsRequestIdRef.current) return;

      const contacts = mapGroupParticipantsToContactItems(response?.data);
      setGroupContacts(contacts);
      setFormSelectedContacts(new Set(contacts.map((c) => contactSelectionKey(c))));
      const emptyMsg = resolveGroupParticipantsEmptyMessage(response, contacts.length);
      setFormError(emptyMsg);
    } catch (err: unknown) {
      if (requestId !== groupParticipantsRequestIdRef.current) return;
      const code = (err as { code?: string; name?: string })?.code ?? (err as { name?: string })?.name;
      if (code === 'ERR_CANCELED' || code === 'CanceledError' || code === 'AbortError') return;
      console.error('[Broadcast] Error loading group participants:', err);
      setGroupContacts([]);
      setFormSelectedContacts(new Set());
      setFormError(resolveGroupParticipantsErrorMessage(err));
    } finally {
      if (requestId === groupParticipantsRequestIdRef.current) {
        setContactsLoading(false);
      }
    }
  };

  const handleToggleContact = (selectionKey: string) => {
    setFormSelectedContacts(prev => {
      const next = new Set(prev);
      if (next.has(selectionKey)) next.delete(selectionKey);
      else next.add(selectionKey);
      return next;
    });
  };

  const handleSelectAll = () => {
    const displayedContacts = displayedFormContacts;
    const allSelected = displayedContacts.every(c => formSelectedContacts.has(contactSelectionKey(c)));
    if (allSelected) {
      setFormSelectedContacts(new Set());
    } else {
      setFormSelectedContacts(new Set(displayedContacts.map(c => contactSelectionKey(c))));
    }
  };

  const handleEditGroupChange = async (groupId: number | null) => {
    const requestId = ++editGroupParticipantsRequestIdRef.current;

    setEditSelectedGroupId(groupId);
    setEditSelectedContacts(new Set());
    setEditGroupContacts([]);

    if (!groupId) {
      return;
    }

    const sid = editingBroadcast?.broadcast?.sector_id || sectorId;
    if (!sid) return;

    const apiGroupId = resolveGroupParticipantsApiId(editGroups, groupId);
    if (!apiGroupId) {
      return;
    }

    const cached = getCachedGroupParticipants(sid, apiGroupId);
    if (cached?.data?.length) {
      const cachedContacts = mapGroupParticipantsToContactItems(cached.data);
      if (requestId === editGroupParticipantsRequestIdRef.current && cachedContacts.length > 0) {
        setEditGroupContacts(cachedContacts);
        setEditSelectedContacts(selectionKeysForGroupContacts(cachedContacts, editPendingRecipientKeysRef.current));
      }
    }

    setEditContactsLoading(true);
    try {
      const needsRefresh = !(cached?.data?.length);
      const response = await fetchGroupParticipantsDeduped(
        sid,
        apiGroupId,
        (refresh, signal) => getWhatsAppGroupParticipants(sid, apiGroupId, { refresh, signal }),
        { refresh: needsRefresh }
      );
      if (requestId !== editGroupParticipantsRequestIdRef.current) return;

      const contacts = mapGroupParticipantsToContactItems(response?.data);
      setEditGroupContacts(contacts);
      setEditSelectedContacts(selectionKeysForGroupContacts(contacts, editPendingRecipientKeysRef.current));
    } catch (err: unknown) {
      if (requestId !== editGroupParticipantsRequestIdRef.current) return;
      console.error('[Broadcast] Error loading group participants:', err);
      setEditGroupContacts([]);
      setEditSelectedContacts(new Set());
    } finally {
      if (requestId === editGroupParticipantsRequestIdRef.current) {
        setEditContactsLoading(false);
      }
    }
  };

  const handleEditToggleContact = (selectionKey: string) => {
    setEditSelectedContacts(prev => {
      const next = new Set(prev);
      if (next.has(selectionKey)) next.delete(selectionKey);
      else next.add(selectionKey);
      return next;
    });
  };

  const handleEditSelectAll = () => {
    const displayed = displayedEditContacts;
    const allSelected = displayed.length > 0 && displayed.every(c => editSelectedContacts.has(contactSelectionKey(c)));
    if (allSelected) {
      setEditSelectedContacts(new Set());
    } else {
      setEditSelectedContacts(new Set(displayed.map(c => contactSelectionKey(c))));
    }
  };

  const insertVariableInFormVariant = (variable: string, index: number) => {
    setFormVariants(prev => prev.map((v, i) => {
      if (i !== index || v.media_type !== 'text') return v;
      return { ...v, content: v.content + variable };
    }));
  };

  const insertVariableInEditVariant = (variable: string, index: number) => {
    setEditVariants(prev => prev.map((v, i) => {
      if (i !== index || v.media_type !== 'text') return v;
      return { ...v, content: v.content + variable };
    }));
  };

  const handleAiOptimizeFormFirstVariant = async () => {
    const sectorId = selectedSectorId ?? 0;
    if (!sectorId) {
      alert('Selecione uma conexão primeiro.');
      return;
    }
    setAiOptimizing('message');
    try {
      const text = formVariants[0]?.media_type === 'text' ? formVariants[0].content : '';
      const result = await optimizeText(sectorId, 'message', text, formAiAgentId ?? undefined, undefined, 'campaign');
      setFormVariants(prev => applyAiOptimizeToFirstTextVariant(prev, result));
    } catch (e: unknown) {
      alert((e as Error)?.message || 'Não foi possível otimizar com IA.');
    } finally {
      setAiOptimizing(null);
    }
  };

  const handleAiOptimizeEditFirstVariant = async () => {
    const sectorId = selectedSectorId ?? 0;
    if (!sectorId) {
      alert('Selecione uma conexão primeiro.');
      return;
    }
    setAiOptimizing('message');
    try {
      const text = editVariants[0]?.media_type === 'text' ? editVariants[0].content : '';
      const result = await optimizeText(sectorId, 'message', text, editAiAgentId ?? undefined, undefined, 'campaign');
      setEditVariants(prev => applyAiOptimizeToFirstTextVariant(prev, result));
    } catch (e: unknown) {
      alert((e as Error)?.message || 'Não foi possível otimizar com IA.');
    } finally {
      setAiOptimizing(null);
    }
  };

  const handleAiOptimize = async (
    type: 'subject' | 'body' | 'message',
    getText: () => string,
    setText: (v: string) => void,
    agentId?: number | null
  ) => {
    const sectorId = selectedSectorId ?? 0;
    if (!sectorId) {
      alert('Selecione uma conexão primeiro.');
      return;
    }
    setAiOptimizing(type);
    try {
      const text = getText();
      const result = await optimizeText(sectorId, type, text, agentId ?? undefined, undefined, 'campaign');
      setText(result);
    } catch (e: unknown) {
      alert((e as Error)?.message || 'Não foi possível otimizar com IA.');
    } finally {
      setAiOptimizing(null);
    }
  };

  const handleCreate = async () => {
    setFormError(null);
    const validVariants = formVariants.filter(isBroadcastVariantValid);
    if (validVariants.length === 0) {
      setFormError('Adicione pelo menos uma variação de mensagem (texto ou áudio)');
      return;
    }
    if (!selectedGroupId) {
      setFormError('Selecione um grupo do WhatsApp');
      return;
    }
    const selectedContacts = groupContacts
      .filter(c => formSelectedContacts.has(contactSelectionKey(c)))
      .map(c => ({ id: c.id, name: c.name, number: c.number }));
    if (selectedContacts.length === 0) {
      setFormError('Selecione pelo menos um contato');
      return;
    }
    if (recurrenceType === 2 && recurrenceWeekdays.length === 0) {
      setFormError('Na recorrência semanal, selecione pelo menos um dia da semana');
      return;
    }
    if (recurrenceType === 3 && recurrenceMonths.length === 0) {
      setFormError('Na recorrência mensal, selecione pelo menos um mês');
      return;
    }
    if (recurrenceType !== 0 && !scheduledTime?.trim()) {
      setFormError('Selecione o horário de envio');
      return;
    }
    if (!timezone?.trim()) {
      setFormError('O fuso horário é obrigatório. Defina-o no seu perfil.');
      return;
    }

    setCreating(true);
    try {
      const resolvedGroupId = resolveGroupParticipantsApiId(groups, selectedGroupId) ?? undefined;
      const messageVariants = await prepareBroadcastVariantsForApi(validVariants);

      const result = await createBroadcast({
        sector_id: sectorId!,
        name: formName || `Campanha ${new Date().toLocaleDateString('pt-BR')}`,
        group_id: resolvedGroupId,
        contact_ids: [],
        contacts: selectedContacts,
        message_variants: messageVariants,
        use_active_ai: true,
        ai_reply_enabled: formAiReplyEnabled,
        ai_agent_id: formAiAgentId != null && formAiAgentId > 0 ? formAiAgentId : undefined,
        recurrence_type: recurrenceType,
        recurrence_interval: recurrenceInterval,
        recurrence_end_date: recurrenceEndDate.trim() || '',
        recurrence_weekdays: recurrenceType === 2 ? recurrenceWeekdays.sort((a, b) => a - b).join(',') : '',
        recurrence_month_days: recurrenceType === 3 ? recurrenceMonths.sort((a, b) => a - b).join(',') : '',
        exclude_weekends: excludeWeekends,
        exclude_holidays: excludeHolidays,
        scheduled_time: (recurrenceType === 0 ? scheduledTime?.trim() || '09:00' : scheduledTime) || '09:00',
        timezone,
      });

      if (result) {
        setShowCreateModal(false);
        await loadBroadcasts();
      }
    } catch (err: any) {
      console.error('Error creating broadcast:', err);
      const msg = err?.response?.data?.message || 'Não foi possível criar a campanha';
      setFormError(msg);
    } finally {
      setCreating(false);
    }
  };

  const handleBroadcastStartClick = (e: React.MouseEvent, b: Broadcast) => {
    e.stopPropagation();
    if (b.status === 'pending' && startShortcutArmedBroadcastId === b.id) {
      e.preventDefault();
      setStartShortcutArmedBroadcastId(null);
      setLidValidationBroadcastId(b.id);
      setLidValidationReport(null);
      setLidValidationStartError(null);
      setLidValidationOpen(true);
      setLidValidationLoading(true);
      validateBroadcastLids(b.id, 'pending')
        .then(setLidValidationReport)
        .catch((err: unknown) => {
          const ax = err as { message?: string; response?: { data?: { message?: string } } };
          setLidValidationReport({
            broadcast_id: b.id,
            sector_id: b.sector_id,
            scope: 'pending',
            total: 0,
            ok_count: 0,
            fail_count: 0,
            skipped_count: 0,
            client_ready: false,
            error: ax?.response?.data?.message || ax?.message || 'Falha na requisição de verificação de LID',
            pending_in_db: 0,
            entries: [],
            seconds_elapsed: 0,
          });
        })
        .finally(() => setLidValidationLoading(false));
      return;
    }
    openConfirm('start', b.id, e);
  };

  const openConfirm = (action: ConfirmActionType, id: number, e: React.MouseEvent, broadcastStatus?: string) => {
    e.stopPropagation();
    setConfirmActionError(null);
    setConfirmAction(action);
    setConfirmBroadcastId(id);
    setConfirmBroadcastStatus(broadcastStatus ?? null);
  };

  const closeConfirm = () => {
    if (!confirmLoading) {
      setConfirmAction(null);
      setConfirmBroadcastId(null);
      setConfirmBroadcastStatus(null);
      setConfirmActionError(null);
    }
  };

  const handleLidValidationStart = async () => {
    if (lidValidationBroadcastId == null) return;
    setLidValidationStartError(null);
    setLidValidationLoading(true);
    try {
      const result = await startBroadcast(lidValidationBroadcastId);
      if (!result.ok) {
        setLidValidationStartError(result.message);
        return;
      }
      setLidValidationOpen(false);
      setLidValidationReport(null);
      await loadBroadcasts();
      if (selectedBroadcast?.broadcast.id === lidValidationBroadcastId) {
        const updated = await getBroadcast(lidValidationBroadcastId);
        if (updated) setSelectedBroadcast(updated);
      }
    } finally {
      setLidValidationLoading(false);
    }
  };

  /** Same worker as production, but ProcessBroadcastOpts.ForceValidation — no send-hour / daily-cap gate in worker. */
  const handleLidValidationStartWorkerTest = async () => {
    if (lidValidationBroadcastId == null) return;
    setLidValidationStartError(null);
    setLidValidationLoading(true);
    try {
      const result = await startBroadcastWorkerTest(lidValidationBroadcastId);
      if (!result.ok) {
        setLidValidationStartError(result.message);
        return;
      }
      setLidValidationOpen(false);
      setLidValidationReport(null);
      await loadBroadcasts();
      if (selectedBroadcast?.broadcast.id === lidValidationBroadcastId) {
        const updated = await getBroadcast(lidValidationBroadcastId);
        if (updated) setSelectedBroadcast(updated);
      }
    } finally {
      setLidValidationLoading(false);
    }
  };

  const getBroadcastForConfirm = (): Broadcast | null => {
    if (confirmBroadcastId == null) return null;
    return broadcasts.find(x => x.id === confirmBroadcastId) ?? selectedBroadcast?.broadcast ?? null;
  };

  const handleConfirmAction = async (): Promise<void | boolean> => {
    const id = confirmBroadcastId;
    if (id == null || confirmAction == null) return;
    setConfirmLoading(true);
    setConfirmActionError(null);
    try {
      if (confirmAction === 'start') {
        const result = await startBroadcast(id);
        if (!result.ok) {
          setConfirmActionError(result.message);
          return false;
        }
      } else if (confirmAction === 'rerun') {
        const result = await rerunBroadcast(id);
        if (!result.ok) {
          setConfirmActionError(result.message);
          return false;
        }
      } else if (confirmAction === 'cancel') {
        const stopped = await cancelBroadcast(id);
        if (!stopped) {
          setConfirmActionError('Não foi possível parar a campanha. Verifique a conexão ou tente de novo.');
          return false;
        }
        if (selectedBroadcast?.broadcast.id === id) setSelectedBroadcast(null);
      } else if (confirmAction === 'delete') {
        await deleteBroadcast(id);
        if (selectedBroadcast?.broadcast.id === id) setSelectedBroadcast(null);
      }
      loadBroadcasts();
      if (confirmAction !== 'delete' && selectedBroadcast?.broadcast.id === id) {
        const updated = await getBroadcast(id);
        if (updated) setSelectedBroadcast(updated);
      }
    } finally {
      setConfirmLoading(false);
    }
  };

  const getConfirmConfig = (): { title: string; message: string; confirmLabel: string; danger?: boolean } => {
    const broadcastForConfirm = getBroadcastForConfirm();
    switch (confirmAction) {
      case 'start':
        return { title: 'Iniciar campanha', message: 'As mensagens serão enviadas para todos os destinatários. Continuar?', confirmLabel: 'Iniciar' };
      case 'rerun':
        return {
          title: 'Reenviar campanha',
          message:
            'Os destinatários pendentes e com falha receberão o envio de novo. Quem já recebeu a mensagem não será contatado outra vez. Continuar?',
          confirmLabel: 'Reenviar',
        };
      case 'cancel':
        if (broadcastForConfirm && isBroadcastAutoPaused(broadcastForConfirm)) {
          return {
            title: 'Parar campanha',
            message:
              'Esta campanha voltaria sozinha quando o limite diário renovar ou a janela de envio abrir (7:00 às 21:59, fuso do perfil). Parar cancela a campanha. Depois você pode usar Reenviar para continuar com os destinatários pendentes.',
            confirmLabel: 'Parar',
            danger: true,
          };
        }
        return {
          title: 'Parar campanha',
          message:
            'Isto para a campanha. Os destinatários pendentes só recebem quando você usar Reenviar. Quem já recebeu não receberá mensagens duplicadas.',
          confirmLabel: 'Parar',
          danger: true,
        };
      case 'delete':
        return { title: 'Excluir campanha', message: 'Esta ação não pode ser desfeita. Continuar?', confirmLabel: 'Excluir', danger: true };
      default:
        return { title: '', message: '', confirmLabel: 'Confirmar' };
    }
  };

  const handleViewDetail = async (id: number) => {
    setDetailLoading(true);
    try {
      const data = await getBroadcast(id);
      if (data) setSelectedBroadcast(data);
    } catch (err) {
      console.error('Error loading broadcast details:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  type BroadcastEditLockFields = Pick<Broadcast, 'status' | 'paused_by_daily_limit' | 'paused_by_time_window' | 'next_run_at'>;

  const handleOpenEdit = (broadcastId: number, b?: BroadcastEditLockFields) => {
    if (b && isBroadcastEditLocked(b)) {
      setShowEditBlockedRunningModal(true);
      return;
    }
    setEditingBroadcastId(broadcastId);
    setShowEditModal(true);
    setEditingBroadcast(null);
  };

  useEffect(() => {
    if (!showEditModal || !editingBroadcastId) return;
    setEditError(null);
    getBroadcast(editingBroadcastId).then((data) => {
      if (data?.broadcast && isBroadcastEditLocked(data.broadcast)) {
        setShowEditModal(false);
        setEditingBroadcastId(null);
        setEditingBroadcast(null);
        setShowEditBlockedRunningModal(true);
        return;
      }
      if (data) {
        setEditingBroadcast(data);
        setEditName(data.broadcast.name);
        setEditVariants(
          data.messages.length
            ? data.messages.map(m => broadcastVariantFromApiMessage(m))
            : [createEmptyBroadcastVariant()]
        );
        setEditRecurrenceType(() => {
          const rt = data.broadcast.recurrence_type;
          if (rt === 0 || rt === 1 || rt === 2 || rt === 3) return rt;
          return 1;
        });
        setEditRecurrenceInterval(Math.max(1, data.broadcast.recurrence_interval ?? 1));
        setEditRecurrenceEndDate(data.broadcast.recurrence_end_date ? data.broadcast.recurrence_end_date.slice(0, 10) : '');
        setEditRecurrenceWeekdays(data.broadcast.recurrence_weekdays ? data.broadcast.recurrence_weekdays.split(',').map(s => parseInt(s, 10)).filter(n => !isNaN(n)) : []);
        setEditRecurrenceMonths(data.broadcast.recurrence_month_days ? data.broadcast.recurrence_month_days.split(',').map(s => parseInt(s, 10)).filter(n => !isNaN(n) && n >= 1 && n <= 12) : []);
        setEditExcludeWeekends(data.broadcast.exclude_weekends ?? false);
        setEditExcludeHolidays(data.broadcast.exclude_holidays ?? false);
        setEditScheduledTime(data.broadcast.scheduled_time || '09:00');
        setEditAiAgentId(data.broadcast.ai_agent_id ?? null);
        setEditAiReplyEnabled(data.broadcast.ai_reply_enabled !== false);
        setEditSelectedContacts(new Set());
        setEditSelectedGroupId(null);
        setEditGroupContacts([]);
        setEditContactSearch('');
        const sid = data.broadcast.sector_id;
        const recipientsLockedToImportList = broadcastRecipientsFromImportedList(data.broadcast);
        const token = SessionService.getToken();
        const recipientsSnapshot = data.recipients || [];
        if (recipientsLockedToImportList) {
          setEditContactsLoading(false);
          setEditGroups([]);
          setEditSelectedContacts(new Set());
          setEditGroupContacts([]);
          setEditSelectedGroupId(null);
          editPendingRecipientKeysRef.current = new Set();
          if (token) {
            listUnifiedAgents(sid, token, 'campaign')
              .then(agentsList => setEditAiCampaignAgents(Array.isArray(agentsList) ? agentsList.filter((a: any) => a.isEnabled !== false) : []))
              .catch(() => setEditAiCampaignAgents([]));
          } else {
            setEditAiCampaignAgents([]);
          }
        } else {
          editPendingRecipientKeysRef.current = recipientSnapshotToSelectionKeys(recipientsSnapshot);
          setEditSelectedContacts(new Set(editPendingRecipientKeysRef.current));
          setEditContactsLoading(true);
          Promise.all([
            getWhatsAppGroups(sid).then((res: any) => (Array.isArray(res?.data) ? res.data : [])),
            token ? listUnifiedAgents(sid, token, 'campaign').catch(() => []).then((list: any) => Array.isArray(list) ? list.filter((a: any) => a.isEnabled !== false) : []) : Promise.resolve([]),
          ])
            .then(([groupsList, agentsList]) => {
              setEditGroups(groupsList);
              setEditAiCampaignAgents(Array.isArray(agentsList) ? agentsList.filter((a: any) => a.isEnabled !== false) : []);
            })
            .catch(() => {
              setEditGroups([]);
              setEditSelectedContacts(new Set());
            })
            .finally(() => setEditContactsLoading(false));
        }
      }
    });
  }, [showEditModal, editingBroadcastId]);

  const handleEditSave = async () => {
    if (editingBroadcastId == null) return;
    setEditError(null);
    const recipientsLockedToImportList = editingBroadcast
      ? broadcastRecipientsFromImportedList(editingBroadcast.broadcast)
      : false;
    if (!recipientsLockedToImportList && !editSelectedGroupId) {
      setEditError('Selecione um grupo do WhatsApp');
      return;
    }
    if (!recipientsLockedToImportList && editSelectedContacts.size === 0) {
      setEditError('Selecione pelo menos um destinatário');
      return;
    }
    if (editRecurrenceType === 2 && editRecurrenceWeekdays.length === 0) {
      setEditError('Na recorrência semanal, selecione pelo menos um dia da semana');
      return;
    }
    if (editRecurrenceType === 3 && editRecurrenceMonths.length === 0) {
      setEditError('Na recorrência mensal, selecione pelo menos um mês');
      return;
    }
    if (editRecurrenceType !== 0 && !editScheduledTime?.trim()) {
      setEditError('Selecione o horário de envio');
      return;
    }
    setEditSaving(true);
    try {
      const selectedList = editGroupContacts.filter(c => editSelectedContacts.has(contactSelectionKey(c)));
      const payload: UpdateBroadcastRequest = {
        name: editName,
        recurrence_type: editRecurrenceType,
        recurrence_interval: editRecurrenceInterval,
        recurrence_end_date: editRecurrenceEndDate.trim() || '',
        recurrence_weekdays:
          editRecurrenceType === 2 ? editRecurrenceWeekdays.sort((a, b) => a - b).join(',') : '',
        recurrence_month_days:
          editRecurrenceType === 3 ? editRecurrenceMonths.sort((a, b) => a - b).join(',') : '',
        exclude_weekends: editExcludeWeekends,
        exclude_holidays: editExcludeHolidays,
        scheduled_time: (editRecurrenceType === 0 ? editScheduledTime?.trim() || '09:00' : editScheduledTime) || '09:00',
        ...(recipientsLockedToImportList
          ? {}
          : { contacts: selectedList.map(c => ({ id: c.id, name: c.name, number: c.number })) }),
      };
      const tzPayload = timezone?.trim() || editingBroadcast?.broadcast?.timezone?.trim();
      if (tzPayload) payload.timezone = tzPayload;
      payload.use_active_ai = true;
      payload.ai_agent_id = editAiAgentId != null && editAiAgentId > 0 ? editAiAgentId : null;
      payload.ai_reply_enabled = editAiReplyEnabled;
      const validVariants = editVariants.filter(isBroadcastVariantValid);
      if (validVariants.length > 0) {
        payload.message_variants = await prepareBroadcastVariantsForApi(validVariants);
      }
      await updateBroadcast(editingBroadcastId, payload);
      setShowEditModal(false);
      setEditingBroadcastId(null);
      if (selectedBroadcast?.broadcast.id === editingBroadcastId) {
        const updated = await getBroadcast(editingBroadcastId);
        if (updated) setSelectedBroadcast(updated);
      }
      loadBroadcasts();
    } catch (err: any) {
      console.error('Error updating broadcast:', err);
      const msg = err?.response?.data?.message || 'Não foi possível salvar';
      setEditError(msg);
    } finally {
      setEditSaving(false);
    }
  };

  /** Dates/times in the user's profile timezone when set (same idea as WhatsApp campaign scheduling). */
  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return String(dateStr);
    const tz = timezone?.trim();
    try {
      return new Intl.DateTimeFormat('pt-BR', {
        dateStyle: 'medium',
        timeStyle: 'medium',
        ...(tz ? { timeZone: tz } : {}),
      }).format(d);
    } catch {
      return d.toLocaleString('pt-BR');
    }
  };

  const formatDateShort = (dateStr: string | null | undefined) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return String(dateStr);
    const tz = timezone?.trim();
    try {
      return new Intl.DateTimeFormat('pt-BR', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        ...(tz ? { timeZone: tz } : {}),
      }).format(d);
    } catch {
      return d.toLocaleString('pt-BR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    }
  };

  const getListLastRunLabel = (b: Broadcast): string => {
    if (b.last_execution_at) return formatDateShort(b.last_execution_at);
    if (b.completed_at) return formatDateShort(b.completed_at);
    if ((b.recurrence_type ?? 0) === 0) return formatDate(b.created_at);
    return '—';
  };

  const getNextRunLabel = (b: Broadcast): string => {
    const datePart = b.next_run_at
      ? formatDateShort(b.next_run_at)
      : ((b.recurrence_type ?? 0) > 0 ? '— (agendada)' : '—');
    const w = b.human_next_window?.trim();
    if (w) {
      return `${datePart} · ${w}`;
    }
    return datePart;
  };

  const getProgressPercent = (b: Broadcast) => {
    if (b.total_count === 0) return 0;
    return Math.round(((b.sent_count + b.failed_count) / b.total_count) * 100);
  };

  const getStatusBadgeClass = (b: Broadcast) => {
    if ((b.recurrence_type ?? 0) > 0 && b.status === 'completed') return 'pending';
    if (isBroadcastAutoPaused(b)) return 'running';
    return b.status;
  };

  const getStatusBadgeLabel = (b: Broadcast) => {
    if ((b.recurrence_type ?? 0) > 0 && b.status === 'completed') return broadcastStatusLabel('pending');
    if (isBroadcastAutoPaused(b)) {
      if (b.paused_by_daily_limit) return 'Em andamento, limite diário (continua sozinha)';
      return 'Em andamento, aguardando a janela de envio (veja Próximo envio)';
    }
    if (b.status === 'paused') return 'Pausada (manual)';
    return broadcastStatusLabel(b.status);
  };

  const renderBroadcastAIBanner = () => {
    if (broadcastAIActive === null) return null;
    const isActive = broadcastAIActive;
    return (
      <div
        className="broadcast-ai-banner"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          padding: '12px 20px',
          margin: '24px 24px 24px',
          borderRadius: 8,
          background: 'linear-gradient(135deg, var(--bg-elevated-strong) 0%, #2e3644 100%)',
          border: '1px solid var(--border-strong)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1 }}>
          {isActive ? (
            <FiCpu size={22} color="var(--text-primary)" />
          ) : (
            <FiAlertCircle size={22} color="var(--text-muted)" />
          )}
          <div>
            <strong style={{ color: 'var(--text-primary)', fontSize: 14 }}>
              {isActive ? 'IA de campanha ativa' : 'IA de campanha não configurada'}
            </strong>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-secondary)', opacity: 0.9 }}>
              {isActive
                ? 'Os leads que responderem às campanhas serão atendidos automaticamente pela IA.'
                : 'Ative para a IA responder automaticamente quando os leads responderem às suas campanhas.'}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => navigate('/ai-agent')}
          style={{
            padding: '8px 16px',
            borderRadius: 6,
            border: 'none',
            background: 'linear-gradient(180deg, var(--bg-card) 0%, var(--bg-base) 100%)',
            color: 'var(--text-primary)',
            fontWeight: 600,
            fontSize: 13,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          {isActive ? 'Configurar' : 'Configurar IA de campanha'}
        </button>
      </div>
    );
  };

  const renderWhatsAppCampaignOverlays = () => (
    <>
      <ExportGroupContactsModal
        open={showExportGroupsModal}
        sectorId={selectedSectorId ?? 0}
        onClose={() => setShowExportGroupsModal(false)}
        onExported={(info) => {
          if (info.contactCount > 0) {
            alert(
              info.groupsFailed > 0
                ? `${info.contactCount} contatos exportados (${info.groupsProcessed} grupos OK, ${info.groupsFailed} com falha).`
                : `${info.contactCount} contatos exportados de ${info.groupsProcessed} ${info.groupsProcessed === 1 ? 'grupo' : 'grupos'}.`
            );
          }
        }}
      />
      {lidValidationOpen && (
        <div
          className="broadcast-modal-overlay"
          onClick={() => !lidValidationLoading && setLidValidationOpen(false)}
        >
          <div
            className="broadcast-modal"
            onClick={e => e.stopPropagation()}
            style={{ maxWidth: 720, width: '95vw' }}
          >
            <div className="broadcast-modal-header">
              <h2>Verificação de LID</h2>
              <button
                type="button"
                className="broadcast-modal-close"
                disabled={lidValidationLoading}
                onClick={() => setLidValidationOpen(false)}
              >
                ×
              </button>
            </div>
            <div className="broadcast-modal-body" style={{ paddingTop: 24 }}>
              <p
                style={{
                  fontSize: 13,
                  color: 'var(--text-tertiary)',
                  lineHeight: 1.55,
                  margin: '0 0 16px 0',
                }}
              >
                Os destinatários pendentes usam o mesmo filtro do envio. <strong>OK</strong> quer dizer que a linha passou
                na verificação: com a <strong>migração de LID ligada</strong>, exige cadastro no WhatsApp e um LID no
                armazenamento local depois do pré-aquecimento e das consultas; com a <strong>migração desligada</strong>, exige só
                o cadastro no WhatsApp (<code>IsOnWhatsApp</code>). O LID local pode continuar vazio e o log pode mostrar NO_LID.
                Nada aqui garante que o WhatsApp não vá responder “no LID found” na hora do envio. Use{' '}
                <strong>Iniciar agora (teste)</strong> para enfileirar o mesmo envio fora do horário comercial e do limite diário
                (mensagens reais; veja as dicas dos botões).
              </p>
              {lidValidationLoading && !lidValidationReport ? (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '8px 0 28px',
                    justifyContent: 'center',
                  }}
                >
                  <LoadingSpinner size="md" />
                  <span>Verificando…</span>
                </div>
              ) : lidValidationReport ? (
                <>
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 12,
                      marginBottom: 12,
                      fontSize: 13,
                      fontFamily: 'system-ui, sans-serif',
                    }}
                  >
                    <span>
                      <strong>OK</strong> {lidValidationReport.ok_count}
                    </span>
                    <span>
                      <strong>Falhas</strong> {lidValidationReport.fail_count}
                    </span>
                    <span>
                      <strong>Ignorados</strong> {lidValidationReport.skipped_count}
                    </span>
                    <span>
                      <strong>Total</strong> {lidValidationReport.total}
                    </span>
                    <span>
                      <strong>Cliente</strong> {lidValidationReport.client_ready ? 'pronto' : 'não pronto'}
                    </span>
                  </div>
                  {lidValidationStartError ? (
                    <div className="confirm-modal__error" style={{ marginBottom: 10 }}>
                      {lidValidationStartError}
                    </div>
                  ) : null}
                  <pre
                    style={{
                      margin: 0,
                      padding: 12,
                      maxHeight: '50vh',
                      overflow: 'auto',
                      background: 'var(--bg-base)',
                      color: 'var(--text-secondary)',
                      fontSize: 11,
                      lineHeight: 1.45,
                      borderRadius: 8,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {formatLidValidationLog(lidValidationReport)}
                  </pre>
                </>
              ) : null}
            </div>
            <div className="broadcast-modal-footer" style={{ flexWrap: 'wrap', gap: 8 }}>
              <button
                type="button"
                className="btn-secondary"
                disabled={lidValidationLoading}
                onClick={() => setLidValidationOpen(false)}
              >
                Fechar
              </button>
              <button
                type="button"
                className="btn-secondary"
                disabled={lidValidationLoading || !lidValidationReport}
                title="Respeita a janela de envio (7:00 às 21:59, fuso do perfil) e o limite diário antes de enfileirar o envio."
                onClick={() => void handleLidValidationStart()}
              >
                {lidValidationLoading ? 'Iniciando…' : 'Iniciar campanha (normal)'}
              </button>
              <button
                type="button"
                className="btn-success"
                disabled={lidValidationLoading || !lidValidationReport}
                title="Enfileira o envio real agora, ignorando a janela de envio e o limite diário (mantém o intervalo entre leads e envia de verdade)."
                onClick={() => void handleLidValidationStartWorkerTest()}
              >
                {lidValidationLoading ? 'Iniciando…' : 'Iniciar agora (teste)'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        open={!!confirmAction}
        onClose={closeConfirm}
        onConfirm={handleConfirmAction}
        title={confirmAction ? getConfirmConfig().title : ''}
        message={
          confirmAction ? (
            <>
              <div>{getConfirmConfig().message}</div>
              {confirmActionError ? <div className="confirm-modal__error">{confirmActionError}</div> : null}
            </>
          ) : (
            ''
          )
        }
        confirmLabel={confirmAction ? getConfirmConfig().confirmLabel : 'Confirmar'}
        cancelLabel="Cancelar"
        variant={confirmAction && getConfirmConfig().danger ? 'danger' : 'default'}
        loading={confirmLoading}
      />

      <SharedModal
        open={showEditBlockedRunningModal}
        onClose={() => setShowEditBlockedRunningModal(false)}
        title="Não é possível editar a campanha agora"
        size="sm"
        variant="default"
        footer={
          <div className="confirm-modal__footer">
            <button
              type="button"
              className="confirm-modal__btn confirm-modal__btn--cancel"
              onClick={() => setShowEditBlockedRunningModal(false)}
            >
              Fechar
            </button>
          </div>
        }
      >
        <div className="confirm-modal__message">
          <p>
            Esta campanha ainda está ativa. As mensagens podem estar sendo enviadas agora, ou ela está aguardando para continuar depois do limite diário ou da janela de envio permitida (7:00 às 21:59, no fuso do seu perfil).
          </p>
          <p>
            Não é possível editar enquanto isso acontece. Use <strong>Parar</strong> para cancelar a campanha se precisar mudar a configuração. Se não, espere até ela ficar parada (concluída, cancelada ou sem pausa automática).
          </p>
        </div>
      </SharedModal>

      {/* Edit Broadcast Modal */}
      {showEditModal && (
        <div className="broadcast-modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="broadcast-modal" onClick={(e) => e.stopPropagation()}>
            <div className="broadcast-modal-header">
              <h2>Editar campanha</h2>
              <button type="button" className="broadcast-modal-close" onClick={() => setShowEditModal(false)}>×</button>
            </div>
            <div className="broadcast-modal-body">
              {!editingBroadcast ? (
                <div style={{ padding: 24, textAlign: 'center' }}>Carregando...</div>
              ) : (
                <>
                  <div className="form-group">
                    <label>Nome da campanha</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Ex.: Promoção de janeiro"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                    />
                  </div>

                  {broadcastRecipientsFromImportedList(editingBroadcast.broadcast) ? (
                    <div className="form-group">
                      <label>Destinatários</label>
                      <div className="form-hint" style={{ marginTop: 4 }}>
                        Esta campanha usa uma lista de contatos importada (planilha). A seleção por grupo ou pelo CRM não se aplica, os destinatários ficam como foram enviados.
                      </div>
                      <div style={{ marginTop: 8, fontSize: 14, color: 'var(--text-secondary)' }}>
                        {editingBroadcast.broadcast.total_count} {editingBroadcast.broadcast.total_count === 1 ? 'destinatário' : 'destinatários'} nesta campanha
                      </div>
                    </div>
                  ) : (
                    <div className="form-group">
                      <label>Selecionar grupo <span className="required">*</span></label>
                      <VirtualizedBroadcastGroupPicker
                        groups={editGroups}
                        value={editSelectedGroupId}
                        onChange={(id) => void handleEditGroupChange(id)}
                        disabled={editContactsLoading}
                        requireGroup
                        placeholder="Selecione um grupo do WhatsApp…"
                      />
                      <div className="form-hint">
                        Escolha um grupo do WhatsApp para carregar e ajustar os membros como destinatários.
                      </div>
                    </div>
                  )}

                  <div className="form-group broadcast-ai-form-group">
                    <div className="broadcast-ai-stack">
                      <label className="broadcast-ai-field-label" htmlFor="broadcast-edit-ai-agent">
                        <FiCpu size={16} aria-hidden /> Funcionário de IA
                      </label>
                      <select
                        id="broadcast-edit-ai-agent"
                        className="form-select"
                        value={editAiAgentId ?? ''}
                        onChange={(e) => {
                          const v = e.target.value ? Number(e.target.value) : null;
                          setEditAiAgentId(v);
                        }}
                      >
                        <option value="">-- IA da conexão (sem funcionário dedicado) --</option>
                        {editAiCampaignAgents.map(a => (
                          <option key={a.id} value={a.id}>{a.name} {a.isEnabled ? '' : '(inativo)'}</option>
                        ))}
                      </select>
                      <p className="broadcast-ai-hint">
                        Opcional, usado só nas respostas. Vazio = IA da conexão com o contexto desta campanha.
                      </p>
                      <label className="broadcast-ai-reply-toggle">
                        <input
                          type="checkbox"
                          checked={editAiReplyEnabled}
                          onChange={(e) => setEditAiReplyEnabled(e.target.checked)}
                        />
                        <span className="broadcast-ai-reply-toggle__copy">
                          <span className="broadcast-ai-reply-toggle__title">A IA responde depois de cada envio</span>
                          <span className="broadcast-ai-reply-toggle__desc">
                            Ligado: cada envio mantém o contato com a IA. Desligado: atendimento humano depois de cada envio.
                          </span>
                        </span>
                      </label>
                    </div>
                  </div>

                  {!broadcastRecipientsFromImportedList(editingBroadcast.broadcast) && (
                    <div className="form-group">
                      <label>Destinatários <span className="required">*</span></label>
                      <div className="contact-selection">
                        <div className="contact-selection-header">
                          {displayedEditContacts.length > 0 && !editContactsLoading && (
                            <label>
                              <input
                                type="checkbox"
                                checked={displayedEditContacts.every(c => editSelectedContacts.has(contactSelectionKey(c)))}
                                onChange={handleEditSelectAll}
                              />
                              {' '}Selecionar todos
                            </label>
                          )}
                          <span className="contact-selection-count">
                            {displayedEditSelectedCount} {displayedEditSelectedCount === 1 ? 'selecionado' : 'selecionados'}
                          </span>
                        </div>
                        <div className="contact-search">
                          <input
                            type="text"
                            placeholder="Buscar contatos..."
                            value={editContactSearch}
                            onChange={(e) => setEditContactSearch(e.target.value)}
                            disabled={editContactsLoading}
                          />
                        </div>
                        <div className="contact-selection-list">
                          {editContactsLoading ? (
                            <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-tertiary)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                              <LoadingSpinner size="md" />
                              <span>Carregando contatos…</span>
                            </div>
                          ) : !editSelectedGroupId ? (
                            <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)' }}>Selecione um grupo acima para carregar os membros</div>
                          ) : displayedEditContacts.length === 0 ? (
                            <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)' }}>Nenhum contato encontrado neste grupo</div>
                          ) : (
                            <VirtualizedBroadcastContactList
                              contacts={displayedEditContacts}
                              selectedKeys={editSelectedContacts}
                              getSelectionKey={contactSelectionKey}
                              onToggle={handleEditToggleContact}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="form-group">
                    <label><span>Mensagens</span> <span className="required">*</span></label>
                    <BroadcastMessageVariantEditor
                      variants={editVariants}
                      onChange={setEditVariants}
                      onOptimizeFirst={handleAiOptimizeEditFirstVariant}
                      aiOptimizing={aiOptimizing === 'message'}
                      onInsertVariable={insertVariableInEditVariant}
                      previewExampleName={PREVIEW_EXAMPLE_NAME}
                    />
                  </div>

                  <div className="form-group" style={{ marginTop: 16 }}>
                    <label><span>Recorrência</span></label>
                    <select className="form-select" value={editRecurrenceType} onChange={(e) => setEditRecurrenceType(Number(e.target.value))}>
                      <option value={0}>Uma vez (início manual)</option>
                      <option value={1}>Diária</option>
                      <option value={2}>Semanal</option>
                      <option value={3}>Mensal</option>
                    </select>
                      <div className="broadcast-schedule">
                        <div className="broadcast-schedule__title">Agendamento</div>
                        <div className="broadcast-schedule__row broadcast-schedule__row--time-date">
                          <div className="broadcast-schedule__field broadcast-schedule__field--time">
                            <span className="broadcast-schedule__label">Horário</span>
                            <div
                              className="broadcast-schedule__picker-trigger"
                              onClick={() => setShowEditTimePicker(true)}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => e.key === 'Enter' && setShowEditTimePicker(true)}
                            >
                              <span className="broadcast-schedule__picker-icon">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
                                </svg>
                              </span>
                              <span className="broadcast-schedule__picker-value">
                                {editScheduledTime || '09:00'}
                              </span>
                            </div>
                          </div>
                          <div className="broadcast-schedule__field broadcast-schedule__field--date">
                            <span className="broadcast-schedule__label broadcast-schedule__label--optional">
                              Data final
                              <span className="broadcast-schedule__optional-badge">Opcional</span>
                            </span>
                            <div
                              className="broadcast-schedule__picker-trigger"
                              onClick={() => setShowEditDatePicker(true)}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => e.key === 'Enter' && setShowEditDatePicker(true)}
                            >
                              <span className="broadcast-schedule__picker-icon">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                  <line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" />
                                  <line x1="3" y1="10" x2="21" y2="10" />
                                </svg>
                              </span>
                              <span className={`broadcast-schedule__picker-value ${!editRecurrenceEndDate ? 'broadcast-schedule__picker-value--placeholder' : ''}`}>
                                {editRecurrenceEndDate ? dayjs(editRecurrenceEndDate).format('DD/MM/YYYY') : 'dd/mm/aaaa'}
                              </span>
                            </div>
                          </div>
                        </div>
                        {editRecurrenceType === 2 && (
                          <div className="broadcast-schedule__weekdays-wrap">
                            <span className="broadcast-schedule__weekdays-label">Repetir em <span style={{ color: 'var(--error-color)' }}>(obrigatório)</span></span>
                            <div className="broadcast-schedule__weekdays">
                              {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map((label, i) => (
                                <button
                                  key={i}
                                  type="button"
                                  className={`broadcast-schedule__day ${editRecurrenceWeekdays.includes(i) ? 'broadcast-schedule__day--selected' : ''}`}
                                  onClick={() => setEditRecurrenceWeekdays(prev => prev.includes(i) ? prev.filter(d => d !== i) : [...prev, i].sort((a, b) => a - b))}
                                >
                                  {label}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                        {editRecurrenceType === 3 && (
                          <div className="broadcast-schedule__months-wrap">
                            <span className="broadcast-schedule__months-label">Repetir nos meses <span style={{ color: 'var(--error-color)' }}>(obrigatório)</span></span>
                            <div className="broadcast-schedule__months">
                              {['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'].map((label, i) => (
                                <button
                                  key={i}
                                  type="button"
                                  className={`broadcast-schedule__month ${editRecurrenceMonths.includes(i + 1) ? 'broadcast-schedule__month--selected' : ''}`}
                                  onClick={() => setEditRecurrenceMonths(prev => prev.includes(i + 1) ? prev.filter(x => x !== i + 1) : [...prev, i + 1].sort((a, b) => a - b))}
                                >
                                  {label}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                        <div className="broadcast-schedule__options">
                          <label className="broadcast-schedule__option">
                            <input type="checkbox" checked={editExcludeWeekends} onChange={(e) => setEditExcludeWeekends(e.target.checked)} />
                            Pular fins de semana
                          </label>
                          <label className="broadcast-schedule__option">
                            <input type="checkbox" checked={editExcludeHolidays} onChange={(e) => setEditExcludeHolidays(e.target.checked)} />
                            Pular feriados
                          </label>
                        </div>
                        <div className="broadcast-schedule__timezone-row">
                          <span className="broadcast-schedule__label">Fuso horário</span>
                          {timezone ? (
                            <TimezoneSelect
                              value={timezone}
                              onChange={() => {}}
                              disabled
                            />
                          ) : (
                            <div className="broadcast-schedule__tz-warning">
                              <span className="broadcast-schedule__tz-warning-text">
                                Nenhum fuso horário configurado no seu perfil.
                              </span>
                              <button
                                type="button"
                                className="broadcast-schedule__tz-warning-btn"
                                onClick={() => navigate('/profile?tab=settings')}
                              >
                                Ir para Configurações
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                  </div>
                </>
              )}
            </div>
            {editError && (
              <div style={{
                padding: '10px 16px',
                margin: '0 20px 10px',
                background: 'var(--error-bg)',
                border: '1px solid var(--error-border)',
                borderRadius: 8,
                color: 'var(--error-color)',
                fontSize: 13,
              }}>
                {editError}
              </div>
            )}
            {editingBroadcast && (
              <div className="broadcast-modal-footer">
                <button className="btn-secondary" onClick={() => setShowEditModal(false)}>Cancelar</button>
                <button className="btn-primary" onClick={handleEditSave} disabled={editSaving}>{editSaving ? 'Salvando...' : 'Salvar'}</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Schedule Time Picker (Edit) */}
      {showEditTimePicker && (
        <div className="broadcast-datetime-overlay" onClick={() => setShowEditTimePicker(false)}>
          <div className="broadcast-datetime-modal" onClick={(e) => e.stopPropagation()}>
            <div className="broadcast-datetime-header">
              <h3 className="broadcast-datetime-title">Selecionar horário</h3>
              <button type="button" className="broadcast-datetime-close" onClick={() => setShowEditTimePicker(false)}>×</button>
            </div>
            <div className="broadcast-datetime-content">
              <ScheduleTimePicker
                selectedTime={editScheduledTime ? dayjs().hour(parseInt(editScheduledTime.slice(0, 2), 10)).minute(parseInt(editScheduledTime.slice(3, 5), 10)) : dayjs()}
                onChange={(t) => setEditScheduledTime(t.format('HH:mm'))}
                timezone={timezone || undefined}
              />
            </div>
            <div className="broadcast-datetime-actions">
              <button type="button" className="broadcast-datetime-cancel" onClick={() => setShowEditTimePicker(false)}>Cancelar</button>
              <button type="button" className="broadcast-datetime-confirm" onClick={() => setShowEditTimePicker(false)}>Confirmar</button>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Date Picker (Edit) */}
      {showEditDatePicker && (
        <div className="broadcast-datetime-overlay" onClick={() => setShowEditDatePicker(false)}>
          <div className="broadcast-datetime-modal" onClick={(e) => e.stopPropagation()}>
            <div className="broadcast-datetime-header">
              <h3 className="broadcast-datetime-title">Data final (opcional)</h3>
              <button type="button" className="broadcast-datetime-close" onClick={() => setShowEditDatePicker(false)}>×</button>
            </div>
            <div className="broadcast-datetime-content">
              <ScheduleCalendar
                selectedDate={editRecurrenceEndDate ? dayjs(editRecurrenceEndDate) : null}
                onChange={(d) => {
                  setEditRecurrenceEndDate(d.format('YYYY-MM-DD'));
                  setShowEditDatePicker(false);
                }}
                viewMode="day"
              />
            </div>
            <div className="broadcast-datetime-actions">
              <button type="button" className="broadcast-datetime-cancel" onClick={() => setShowEditDatePicker(false)}>Cancelar</button>
              <button type="button" className="broadcast-datetime-confirm" onClick={() => setShowEditDatePicker(false)}>Confirmar</button>
            </div>
          </div>
        </div>
      )}

      {detailLoading && (
        <div
          className="broadcast-modal-overlay"
          onClick={() => {
            setDetailLoading(false);
            setSelectedBroadcast(null);
          }}
          role="button"
          aria-label="Carregando, clique para cancelar"
        >
          <div className="broadcast-loading" onClick={(e) => e.stopPropagation()}>
            <div className="spinner" />
            Carregando detalhes...
            <p style={{ marginTop: 12, fontSize: 13, color: 'var(--text-tertiary)' }}>Clique fora para cancelar</p>
          </div>
        </div>
      )}

    </>
  );

  if (selectedBroadcast) {
    const b = selectedBroadcast.broadcast;
    const progress = getProgressPercent(b);

    return (
      <>
      <div className="broadcast-page">
        <div className="broadcast-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
            <button className="broadcast-detail-back" onClick={() => setSelectedBroadcast(null)}>
              <FiArrowLeft size={16} /> Voltar
            </button>
            <span className="broadcast-recurrence-hint" style={{ fontSize: 13, color: 'var(--text-tertiary)', fontWeight: 500 }}>
              Último envio: {getListLastRunLabel(b)}
              {' • '}
              Próximo envio: {getNextRunLabel(b)}
            </span>
            <span className={`status-badge ${getStatusBadgeClass(b)}`} style={{ marginLeft: 4 }}>
              {getStatusBadgeLabel(b)}
            </span>
          </div>
          <div className="broadcast-header-actions">
            {b.status === 'pending' && !isBroadcastAutoPaused(b) && (
              <button
                type="button"
                className="btn-success"
                style={startShortcutArmedBroadcastId === b.id ? { boxShadow: '0 0 0 2px #166534' } : undefined}
                onClick={(e) => handleBroadcastStartClick(e, b)}
                onContextMenu={(e) => handlePendingStartButtonContextMenu(e, b.id)}
                title={
                  startShortcutArmedBroadcastId === b.id
                    ? 'Atalho ativo. O clique roda a verificação de LID (depois você pode Iniciar)'
                    : undefined
                }
              >
                <FiPlay size={14} /> {startShortcutArmedBroadcastId === b.id ? 'Iniciar (atalho)' : 'Iniciar'}
              </button>
            )}
            {canRerunBroadcast(b) && (
              <button
                type="button"
                className="btn-success"
                onClick={(e) => openConfirm('rerun', b.id, e)}
                title="Continuar o envio para os destinatários pendentes e com falha"
              >
                <FiRefreshCw size={14} /> Reenviar
              </button>
            )}
            {(isBroadcastAutoPaused(b) || b.status === 'paused' || b.status === 'running') && (
              <button className="btn-danger" onClick={(e) => openConfirm('cancel', b.id, e)} title="Parar e cancelar esta campanha">
                <FiSquare size={14} /> Parar
              </button>
            )}
            <button className="btn-icon danger" onClick={(e) => openConfirm('delete', b.id, e)} title="Excluir">
              <FiTrash2 size={16} />
            </button>
            <button className="btn-secondary" onClick={(e) => { e.stopPropagation(); handleOpenEdit(b.id, b); }} title="Editar">
              <FiEdit2 size={14} /> Editar
            </button>
          </div>
        </div>

        {renderBroadcastAIBanner()}

        <div className="broadcast-detail">
          {(b.status === 'paused' || b.status === 'pending') && b.paused_by_daily_limit && (
            <div className="broadcast-limit-banner broadcast-limit-banner--daily" role="status">
              <strong>Limite diário atingido, a campanha continua ativa</strong>
              <p>
                O envio pausa só por hoje (aquecimento da conexão). A campanha volta sozinha quando a contagem diária renovar (no dia seguinte, no fuso da sua conta), entre 7:00 e 21:59 nesse mesmo fuso.
              </p>
            </div>
          )}
          {b.status === 'paused' && b.paused_by_time_window && !b.paused_by_daily_limit && (
            <div className="broadcast-limit-banner broadcast-limit-banner--window" role="status">
              <strong>Fora da janela de envio, a campanha continua ativa</strong>
              <p>
                Os envios só acontecem entre 7:00 e 21:59 no fuso da sua conta. A campanha continua sozinha quando a janela abrir. Use Parar só se quiser cancelar de vez.
              </p>
            </div>
          )}
          <div className="broadcast-detail-stats">
            <div className="stat-card">
              <div className="stat-card-label">Total</div>
              <div className="stat-card-value primary">{b.total_count}</div>
            </div>
            <div className="stat-card">
              <div className="stat-card-label">Enviados</div>
              <div className="stat-card-value success">{b.sent_count}</div>
            </div>
            <div className="stat-card">
              <div className="stat-card-label">Falhas</div>
              <div className="stat-card-value danger">{b.failed_count}</div>
            </div>
            <div className="stat-card">
              <div className="stat-card-label">Pendentes</div>
              <div className="stat-card-value">{b.total_count - b.sent_count - b.failed_count}</div>
            </div>
            {(selectedBroadcast.execution_count ?? selectedBroadcast.executions?.length ?? 0) > 0 && (
              <div className="stat-card">
                <div className="stat-card-label">Execuções</div>
                <div className="stat-card-value primary">{selectedBroadcast.execution_count ?? selectedBroadcast.executions?.length ?? 0}</div>
              </div>
            )}
          </div>

          {b.total_count > 0 && (
            <div className="broadcast-progress" style={{ marginBottom: 24 }}>
              <div className="broadcast-progress-bar">
                <div
                  className={`broadcast-progress-fill ${b.status === 'running' || isBroadcastAutoPaused(b) ? 'running' : 'success'}`}
                  style={{ width: `${progress}%` }}
                />
              </div>
              <div className="broadcast-progress-text">
                <span>{progress}% concluído</span>
                <span>{b.sent_count + b.failed_count} / {b.total_count}</span>
              </div>
            </div>
          )}

          <div className="broadcast-detail-section">
            <div className="broadcast-detail-section-header">Respostas depois do envio</div>
            <div className="broadcast-detail-section-body" style={{ fontSize: 14, color: 'var(--text-secondary)' }}>
              {b.ai_reply_enabled !== false ? (
                <p style={{ margin: 0 }}>
                  <span className="status-badge sent" style={{ marginRight: 8 }}>IA ligada</span>
                  Cada envio mantém ou cria o contato com a IA ligada e as respostas direcionadas à campanha.
                </p>
              ) : (
                <p style={{ margin: 0 }}>
                  <span className="status-badge failed" style={{ marginRight: 8 }}>Humano</span>
                  Cada envio tira o contato da IA da campanha e deixa o atendimento com uma pessoa.
                </p>
              )}
            </div>
          </div>

          <div className="broadcast-detail-section">
            <div className="broadcast-detail-section-header">
              Mensagens ({selectedBroadcast.messages.length} {selectedBroadcast.messages.length === 1 ? 'variação' : 'variações'})
            </div>
            <div className="broadcast-detail-section-body">
              {selectedBroadcast.messages.map((m, i) => (
                <div key={m.id} className="message-preview">
                  <strong>#{i + 1}:</strong> {m.content}
                </div>
              ))}
            </div>
          </div>

          <div className="broadcast-detail-section">
            <div className="broadcast-detail-section-header">
              Destinatários ({selectedBroadcast.recipients.length})
            </div>
            <div className="broadcast-detail-section-body broadcast-detail-recipients-virt" style={{ padding: 0 }}>
              <TableVirtuoso<BroadcastRecipient>
                style={{ height: 420 }}
                data={selectedBroadcast.recipients}
                computeItemKey={(_i, rec) => String(rec.id)}
                components={{
                  Table: ({ style, children }) => (
                    <table className="recipients-table" style={style}>{children}</table>
                  ),
                }}
                fixedHeaderContent={() => (
                  <tr>
                    <th>Nome</th>
                    <th>Número</th>
                    <th>Status</th>
                    <th>Mensagem enviada</th>
                    <th>Horário</th>
                    <th>Observação de entrega</th>
                    <th>Ações</th>
                  </tr>
                )}
                itemContent={(_index, rec) => (
                  <>
                    <td>{getBroadcastTableNameCell(rec.contact_name, rec.number)}</td>
                    <td>{getBroadcastTableNumberCell(rec.number)}</td>
                    <td>
                      <span className={`status-badge ${rec.status}`}>{broadcastStatusLabel(rec.status)}</span>
                    </td>
                    <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {rec.message_used || '-'}
                    </td>
                    <td>{rec.sent_at ? formatDate(rec.sent_at) : '-'}</td>
                    <td style={{ maxWidth: 280, fontSize: 13, color: rec.status === 'failed' ? 'var(--error-color)' : 'var(--text-muted)' }}>
                      {rec.status === 'failed' ? (rec.error?.trim() || '—') : '—'}
                    </td>
                    <td>
                      {rec.status === 'sent' && rec.contact_id > 0 ? (
                        <button
                          type="button"
                          className="broadcast-recipient-open-chat"
                          onClick={() => navigate(`/chat?contactId=${rec.contact_id}`)}
                          title="Abrir este lead na conversa"
                        >
                          <FiMessageCircle size={14} aria-hidden />
                          Abrir conversa
                        </button>
                      ) : (
                        <span className="broadcast-recipient-action-placeholder">—</span>
                      )}
                    </td>
                  </>
                )}
              />
            </div>
          </div>

          {selectedBroadcast.executions && selectedBroadcast.executions.length > 0 && (
            <div className="broadcast-detail-section">
              <div className="broadcast-detail-section-header">
                Execuções ({selectedBroadcast.executions.length}), enviados e recebidos por execução
              </div>
              <div className="broadcast-detail-section-body" style={{ padding: 0 }}>
                <table className="recipients-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Início</th>
                      <th>Fim</th>
                      <th>Status</th>
                      <th>Enviados</th>
                      <th>Falhas</th>
                      <th>Recebidos</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedBroadcast.executions.map((ex: BroadcastExecution, idx: number) => (
                      <tr key={ex.id}>
                        <td>{idx + 1}</td>
                        <td>{formatDate(ex.started_at)}</td>
                        <td>{ex.completed_at ? formatDate(ex.completed_at) : '-'}</td>
                        <td><span className={`status-badge ${ex.status}`}>{broadcastStatusLabel(ex.status)}</span></td>
                        <td>{ex.sent_count}</td>
                        <td>{ex.failed_count}</td>
                        <td>{ex.received_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

        </div>
      </div>
      {renderWhatsAppCampaignOverlays()}
      </>
    );
  }

  if (!selectedSectorId) {
    return (
      <div className="broadcast-page">
        <div className="broadcast-header">
          <h1>Campanhas</h1>
        </div>
        <div className="broadcast-content">
          <div className="broadcast-empty">
            <div className="broadcast-empty-icon" style={{ fontSize: 48 }}><FiZap size={48} /></div>
            <h3>Nenhuma conexão selecionada</h3>
            <p>Selecione uma conexão no menu lateral para gerenciar as campanhas</p>
          </div>
        </div>
      </div>
    );
  }

  if (activeTab === 'email' && selectedEmailBroadcast) {
    const eb = selectedEmailBroadcast.broadcast;
    const emailProgress = getEmailProgressPercent(eb);

    return (
      <div className="broadcast-page">
        <div className="broadcast-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
            <button className="broadcast-detail-back" onClick={() => setSelectedEmailBroadcast(null)}>
              <FiArrowLeft size={16} /> Voltar
            </button>
          </div>
          <div className="broadcast-header-actions">
            {(eb.status === 'draft' || eb.status === 'paused_daily') && (
              <button className="btn-success" onClick={(e) => openEmailConfirm('start', eb.id, e)}>
                <FiPlay size={14} /> {eb.status === 'paused_daily' ? 'Retomar' : 'Iniciar'}
              </button>
            )}
            {(eb.status === 'draft' || eb.status === 'completed' || eb.status === 'cancelled' || eb.status === 'failed' || eb.status === 'paused_daily') && (
              <button className="btn-icon danger" onClick={(e) => openEmailConfirm('delete', eb.id, e)} title="Excluir">
                <FiTrash2 size={16} />
              </button>
            )}
          </div>
        </div>

        {emailBackgroundNotice && (
          <div style={{ margin: '0 24px 16px', padding: '12px 16px', background: 'var(--success-bg)', border: '1px solid var(--success-border)', borderRadius: 8, color: 'var(--success-color)', fontSize: 14 }}>
            {emailBackgroundNotice}
          </div>
        )}

        <div className="broadcast-detail">
          <div className="broadcast-email-detail-meta" style={{ marginBottom: 16, padding: '0 4px', fontSize: 13, color: 'var(--text-tertiary)', lineHeight: 1.5 }}>
            <span><strong>Criada:</strong> {formatDate(eb.createdAt)}</span>
            {eb.startedAt && (
              <>
                {' · '}
                <span><strong>Iniciada:</strong> {formatDate(eb.startedAt)}</span>
              </>
            )}
            {eb.completedAt && (
              <>
                {' · '}
                <span><strong>Concluída:</strong> {formatDate(eb.completedAt)}</span>
              </>
            )}
            {timezone?.trim() && (
              <span style={{ display: 'block', marginTop: 6, fontSize: 12, color: 'var(--text-muted)' }}>
                Datas e horários no fuso do seu perfil ({timezone}).
              </span>
            )}
          </div>

          <div className="broadcast-detail-stats">
            <div className="stat-card">
              <div className="stat-card-label">Total</div>
              <div className="stat-card-value primary">{eb.totalCount}</div>
            </div>
            <div className="stat-card">
              <div className="stat-card-label">Enviados</div>
              <div className="stat-card-value success">{eb.sentCount}</div>
            </div>
            <div className="stat-card">
              <div className="stat-card-label">Falhas</div>
              <div className="stat-card-value danger">{eb.failedCount}</div>
            </div>
            <div className="stat-card">
              <div className="stat-card-label">Pendentes</div>
              <div className="stat-card-value">{eb.totalCount - eb.sentCount - eb.failedCount}</div>
            </div>
          </div>

          {eb.totalCount > 0 && (
            <div className="broadcast-progress" style={{ marginBottom: 24 }}>
              <div className="broadcast-progress-bar">
                <div
                  className={`broadcast-progress-fill ${eb.status === 'running' || eb.status === 'paused_daily' ? 'running' : 'success'}`}
                  style={{ width: `${emailProgress}%` }}
                />
              </div>
              <div className="broadcast-progress-text">
                <span>{emailProgress}% concluído</span>
                <span>{eb.sentCount + eb.failedCount} / {eb.totalCount}</span>
              </div>
            </div>
          )}

          <div className="broadcast-detail-section">
            <div className="broadcast-detail-section-header">
              Assunto
            </div>
            <div className="broadcast-detail-section-body">
              <div className="message-preview">{eb.subject}</div>
            </div>
          </div>

          <div className="broadcast-detail-section">
            <div className="broadcast-detail-section-header">
              Corpo
            </div>
            <div className="broadcast-detail-section-body">
              <div className="message-preview" style={{ whiteSpace: 'pre-wrap' }} dangerouslySetInnerHTML={{ __html: eb.bodyHtml }} />
            </div>
          </div>

          {/* AI Agent Section */}
          {eb.aiAgentId && (
            <div className="broadcast-detail-section">
              <div className="broadcast-detail-section-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span><FiCpu size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />Funcionário de IA</span>
                <button
                  className={eb.useAi ? 'btn-icon danger' : 'btn-success btn-sm'}
                  onClick={async () => {
                    try {
                      await toggleBroadcastAI(eb.id, !eb.useAi);
                      const updated = await getEmailBroadcast(eb.id);
                      if (updated) setSelectedEmailBroadcast(updated);
                    } catch (err) { console.error(err); }
                  }}
                  style={{ fontSize: 12, padding: '4px 12px' }}
                >
                  {eb.useAi ? 'Desativar IA' : 'Ativar IA'}
                </button>
              </div>
              <div className="broadcast-detail-section-body">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span className={`status-badge ${eb.useAi ? 'sent' : 'failed'}`} style={{ fontSize: 12 }}>
                    {eb.useAi ? 'IA ativa' : 'IA desativada'}
                  </span>
                  <span style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>
                    Funcionário: {aiCampaignAgents.find(a => a.id === eb.aiAgentId)?.name || `#${eb.aiAgentId}`}
                  </span>
                </div>
                {eb.useAi && (
                  <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6, marginBottom: 0 }}>
                    O funcionário de IA responde automaticamente quando os destinatários responderem a esta campanha.
                  </p>
                )}
              </div>
            </div>
          )}

          <div className="broadcast-detail-section">
            <div className="broadcast-detail-section-header">
              Destinatários ({selectedEmailBroadcast.recipients.length})
            </div>
            <div className="broadcast-detail-section-body" style={{ padding: 0 }}>
              <table className="recipients-table recipients-table--email-broadcast">
                <thead>
                  <tr>
                    <th>Destinatário</th>
                    <th>Mensagem enviada</th>
                    <th>Resultado</th>
                    <th>Horário</th>
                    <th>Observação de entrega</th>
                    {eb.aiAgentId && <th>IA</th>}
                  </tr>
                </thead>
                <tbody>
                  {selectedEmailBroadcast.recipients.map((rec) => {
                    const failed = (rec.status || '').toLowerCase() === 'failed';
                    const errText = humanizeEmailDeliveryError(rec.errorMessage);
                    return (
                      <tr key={rec.id}>
                        <td>{rec.email}</td>
                        <td
                          className="recipients-table__message-sent"
                          title={eb.subject || ''}
                          style={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        >
                          {eb.subject || '—'}
                        </td>
                        <td>
                          <span className={`status-badge ${emailRecipientStatusClass(rec.status)}`} title={rec.status}>
                            {emailRecipientResultLabel(rec.status)}
                          </span>
                        </td>
                        <td>{rec.sentAt ? formatDate(rec.sentAt) : '—'}</td>
                        <td
                          className={failed ? 'recipients-table__delivery-note recipients-table__delivery-note--error' : 'recipients-table__delivery-note'}
                          style={{ maxWidth: 360, fontSize: 13, color: failed ? 'var(--error-color)' : 'var(--text-muted)' }}
                        >
                          {failed ? (errText || 'Falha no envio (sem detalhes).') : '—'}
                        </td>
                        {eb.aiAgentId && (
                          <td>
                            <label style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer', gap: 4 }}>
                              <input
                                type="checkbox"
                                checked={rec.aiEnabled !== false}
                                onChange={async () => {
                                  try {
                                    await toggleRecipientAI(eb.id, rec.id, rec.aiEnabled === false);
                                    const updated = await getEmailBroadcast(eb.id);
                                    if (updated) setSelectedEmailBroadcast(updated);
                                  } catch (err) { console.error(err); }
                                }}
                                style={{ width: 16, height: 16, cursor: 'pointer' }}
                              />
                              <span style={{ fontSize: 11, color: rec.aiEnabled !== false ? 'var(--success-color)' : 'var(--text-muted)' }}>
                                {rec.aiEnabled !== false ? 'Ligada' : 'Desligada'}
                              </span>
                            </label>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Email confirm action modal */}
        <ConfirmModal
          open={emailConfirmAction !== null}
          onClose={() => { setEmailConfirmAction(null); setEmailConfirmBroadcastId(null); }}
          onConfirm={handleEmailConfirmAction}
          title={emailConfirmAction === 'start' ? 'Iniciar campanha' : emailConfirmAction === 'delete' ? 'Excluir campanha' : ''}
          message={emailConfirmAction === 'start' ? 'Iniciar esta campanha de e-mail? Os e-mails serão enviados para todos os destinatários.' : emailConfirmAction === 'delete' ? 'Excluir esta campanha? Esta ação não pode ser desfeita.' : ''}
          confirmLabel={emailConfirmAction === 'delete' ? 'Excluir' : 'Confirmar'}
          cancelLabel="Cancelar"
          variant={emailConfirmAction === 'delete' ? 'danger' : 'default'}
          loading={emailConfirmLoading}
        />
      </div>
    );
  }

  if (activeTab === 'email' && !hasModule('email')) {
    setActiveTab('campanhas');
  }

  if (isOfficial && activeTab === 'campanhas' && sectorId) {
    return (
      <SparkCampaignsPanel
        sectorId={sectorId}
        onSwitchToEmail={hasModule('email') ? () => setActiveTab('email') : undefined}
        hasEmailModule={hasModule('email')}
      />
    );
  }

  if (activeTab === 'email') {
    return (
      <div className="broadcast-page">
        <div className="broadcast-header">
          <div className="campaign-header-row">
            <div className="campaign-section">
              <h2 className="campaign-section__title">Campanhas</h2>
              <nav className="campaign-tabs">
                <button className="campaign-tabs__item" onClick={() => setActiveTab('campanhas')}>WhatsApp</button>
                <span className="campaign-tabs__separator" />
                <button className="campaign-tabs__item campaign-tabs__item--active" onClick={() => setActiveTab('email')}>E-mail</button>
              </nav>
            </div>
          </div>
          <div className="broadcast-header-actions">
            <button className="btn-secondary" onClick={loadEmailBroadcasts}><FiRefreshCw size={14} /> Atualizar</button>
            <button className="btn-primary" onClick={() => { setShowCreateEmailModal(true); loadEmailContacts(); }}><FiPlus size={14} /> Nova campanha</button>
          </div>
        </div>

        {emailBackgroundNotice && (
          <div style={{ margin: '0 24px 16px', padding: '12px 16px', background: 'var(--success-bg)', border: '1px solid var(--success-border)', borderRadius: 8, color: 'var(--success-color)', fontSize: 14 }}>
            {emailBackgroundNotice}
          </div>
        )}

        <div className="broadcast-content">
          {emailBroadcastsLoading ? (
            <div className="broadcast-loading"><div className="spinner" />Carregando...</div>
          ) : emailBroadcasts.length === 0 ? (
            <div className="broadcast-empty">
              <div className="broadcast-empty-icon"><FiMail size={48} /></div>
              <h3>Nenhuma campanha de e-mail ainda</h3>
              <p>Crie sua primeira campanha para enviar e-mails para vários contatos</p>
            </div>
          ) : (
            <div className="broadcast-list">
              {emailBroadcasts.map(b => {
                const progress = getEmailProgressPercent(b);
                return (
                  <div key={b.id} className="broadcast-card" onClick={() => handleViewEmailDetail(b.id)}>
                    <div className="broadcast-card-header">
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span className="broadcast-card-title">{b.name}</span>
                        <span className={`status-badge ${b.status}`}>{broadcastStatusLabel(b.status)}</span>
                        {b.useAi && <span className="status-badge sent" style={{ fontSize: 10, padding: '2px 6px' }}><FiCpu size={10} /> IA</span>}
                      </div>
                      <div className="broadcast-card-actions">
                        {(b.status === 'draft' || b.status === 'paused_daily') && (
                          <button className="btn-success btn-sm" onClick={(e) => openEmailConfirm('start', b.id, e)}>
                            <FiPlay size={12} /> {b.status === 'paused_daily' ? 'Retomar' : 'Iniciar'}
                          </button>
                        )}
                        {b.status !== 'running' && (
                        <button className="btn-icon danger" onClick={(e) => openEmailConfirm('delete', b.id, e)} title="Excluir">
                          <FiTrash2 size={14} />
                        </button>
                        )}
                      </div>
                    </div>
                    <div className="broadcast-card-meta">
                      <span className="broadcast-meta-item">
                        <FiUsers size={13} /> {b.totalCount} {b.totalCount === 1 ? 'destinatário' : 'destinatários'}
                      </span>
                      <span className="broadcast-meta-item">
                        <FiClock size={13} /> {formatDate(b.createdAt)}
                      </span>
                      <span className="broadcast-meta-item">
                        <FiCheckCircle size={13} color="var(--success-color)" /> {b.sentCount} {b.sentCount === 1 ? 'enviado' : 'enviados'}
                      </span>
                      {b.failedCount > 0 && (
                        <span className="broadcast-meta-item" style={{ color: 'var(--error-color)' }}>
                          <FiXCircle size={13} /> {b.failedCount} {b.failedCount === 1 ? 'falha' : 'falhas'}
                        </span>
                      )}
                    </div>
                    {(b.status === 'running' || b.status === 'paused_daily' || b.status === 'completed') && b.totalCount > 0 && (
                      <div className="broadcast-progress">
                        <div className="broadcast-progress-bar">
                          <div
                            className={`broadcast-progress-fill ${b.status === 'running' || b.status === 'paused_daily' ? 'running' : 'success'}`}
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                        <div className="broadcast-progress-text">
                          <span>{progress}%</span>
                          <span>{b.sentCount + b.failedCount}/{b.totalCount}</span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Create Email Campaign Modal */}
        {showCreateEmailModal && (
          <div className="broadcast-modal-overlay" onClick={() => {
            setShowCreateEmailModal(false);
            setEmailFormName(''); setEmailFormSubject(''); setEmailFormBody(''); setEmailFormSelectedContacts(new Set());
            setEmailFormCc(''); setEmailFormBcc(''); setEmailFormReplyTo(''); setEmailFormAttachments([]); setShowCcBccSection(false); setEmailFormAiAgentId(null); setEmailExcelError(null); setEmailExcelImporting(false);
          }}>
            <div className="broadcast-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560, maxHeight: '90vh', overflowY: 'auto' }}>
              <div className="broadcast-modal-header">
                <h2>Nova campanha de e-mail</h2>
                <button className="close-btn" onClick={() => {
                  setShowCreateEmailModal(false);
                  setEmailFormName(''); setEmailFormSubject(''); setEmailFormBody(''); setEmailFormSelectedContacts(new Set());
                  setEmailFormCc(''); setEmailFormBcc(''); setEmailFormReplyTo(''); setEmailFormAttachments([]); setShowCcBccSection(false); setEmailFormAiAgentId(null); setEmailExcelError(null); setEmailExcelImporting(false);
                }}><FiXCircle size={20} /></button>
              </div>
              <div className="broadcast-modal-body" style={{ padding: 24 }}>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Nome</label>
                  <input type="text" value={emailFormName} onChange={e => setEmailFormName(e.target.value)} placeholder="Nome da campanha" style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--border-color)', borderRadius: 8, fontSize: 14 }} />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Assunto</label>
                  <div className="field-with-optimize">
                    <div className="field-with-optimize__header">
                      <button type="button" className="field-with-optimize__btn" title="Otimizar com IA" disabled={aiOptimizing !== null} onClick={() => handleAiOptimize('subject', () => emailFormSubject, setEmailFormSubject)}>{aiOptimizing === 'subject' ? <LoadingSpinner size="sm" color="white" /> : 'Otimizar'}</button>
                    </div>
                    <input type="text" className="field-with-optimize__input" value={emailFormSubject} onChange={e => setEmailFormSubject(e.target.value)} placeholder="Assunto do e-mail" />
                  </div>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Corpo <span className="required">*</span></label>
                  <div className="form-hint" style={{ marginBottom: 6 }}>
                    Escreva o conteúdo do e-mail em texto simples. Use {'{'}<strong>name</strong>{'}'} para personalizar com o nome de cada destinatário.
                  </div>
                  <div className="form-hint" style={{ marginBottom: 8, fontSize: 12 }}>
                    Variável (clique para inserir):
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
                    <button type="button" onClick={() => setEmailFormBody(prev => prev + '{name}')} style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid var(--primary-color)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: 13, cursor: 'pointer', fontFamily: 'monospace' }}>{'{name}'}</button>
                  </div>
                  <div className="field-with-optimize">
                    <div className="field-with-optimize__header">
                      <button type="button" className="field-with-optimize__btn" title="Otimizar com IA" disabled={aiOptimizing !== null} onClick={() => handleAiOptimize('body', () => emailFormBody, setEmailFormBody)}>{aiOptimizing === 'body' ? <LoadingSpinner size="sm" color="white" /> : 'Otimizar'}</button>
                    </div>
                    <textarea className="field-with-optimize__textarea" value={emailFormBody} onChange={e => setEmailFormBody(e.target.value)} placeholder={"Olá {name},\n\nSeu conteúdo aqui.\n\nAtenciosamente"} rows={6} />
                  </div>
                  {emailFormBody.includes('{name}') && (
                    <div style={{ marginTop: 8, padding: '10px 12px', background: 'var(--bg-tertiary)', borderRadius: 8 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4 }}>Prévia (exemplo: nome &quot;João&quot;):</div>
                      <div style={{ fontSize: 13, color: 'var(--text-primary)', whiteSpace: 'pre-wrap' }}>{emailFormBody.replace(/\{name\}/g, 'João')}</div>
                    </div>
                  )}
                </div>

                {/* CC, BCC, Reply-To */}
                <div style={{ marginBottom: 16 }}>
                  <button
                    type="button"
                    onClick={() => setShowCcBccSection(!showCcBccSection)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none',
                      color: 'var(--text-primary)', fontSize: 13, fontWeight: 500, cursor: 'pointer', padding: 0,
                    }}
                  >
                    {showCcBccSection ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
                    Cc, Cco, Responder para
                  </button>
                  {showCcBccSection && (
                    <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 12, padding: 12, background: 'var(--bg-elevated)', borderRadius: 8, border: '1px solid var(--border-color)' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 4, color: 'var(--text-tertiary)' }}>Cc</label>
                        <input type="text" value={emailFormCc} onChange={e => setEmailFormCc(e.target.value)} placeholder="email1@exemplo.com, email2@exemplo.com" style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--border-color)', borderRadius: 6, fontSize: 13 }} />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 4, color: 'var(--text-tertiary)' }}>Cco</label>
                        <input type="text" value={emailFormBcc} onChange={e => setEmailFormBcc(e.target.value)} placeholder="email1@exemplo.com, email2@exemplo.com" style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--border-color)', borderRadius: 6, fontSize: 13 }} />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 4, color: 'var(--text-tertiary)' }}>Responder para</label>
                        <input type="email" value={emailFormReplyTo} onChange={e => setEmailFormReplyTo(e.target.value)} placeholder="resposta@exemplo.com" style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--border-color)', borderRadius: 6, fontSize: 13 }} />
                      </div>
                    </div>
                  )}
                </div>

                {/* Attachments */}
                <div style={{ marginBottom: 16, display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, width: '100%', textAlign: 'center' }}>Anexos</label>
                  <input
                    type="file"
                    multiple
                    accept="*/*"
                    onChange={(e) => {
                      const files = e.target.files;
                      if (!files?.length) return;
                      const maxSize = 5 * 1024 * 1024;
                      const maxTotal = 15 * 1024 * 1024;
                      const currentTotal = emailFormAttachments.reduce((s, a) => s + a.file.size, 0);
                      const toAdd: Array<{ id: string; file: File }> = [];
                      let newTotal = currentTotal;
                      for (let i = 0; i < files.length; i++) {
                        if (files[i].size > maxSize) continue;
                        if (newTotal + files[i].size > maxTotal) break;
                        toAdd.push({ id: `${Date.now()}-${i}`, file: files[i] });
                        newTotal += files[i].size;
                      }
                      setEmailFormAttachments(prev => [...prev, ...toAdd]);
                      e.target.value = '';
                    }}
                    style={{ display: 'none' }}
                    id="email-attachments-input"
                  />
                  <label
                    htmlFor="email-attachments-input"
                    style={{
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '8px 16px',
                      border: '1px dashed var(--border-strong)', borderRadius: 8, background: 'var(--bg-elevated)',
                      cursor: 'pointer', fontSize: 13, color: 'var(--text-tertiary)', width: '100%',
                    }}
                  >
                    <FiPaperclip size={16} />
                    Adicionar arquivos (até 5 MB cada, 15 MB no total)
                  </label>
                  {emailFormAttachments.length > 0 && (
                    <ul style={{ marginTop: 8, padding: 0, listStyle: 'none', width: '100%' }}>
                      {emailFormAttachments.map((a) => (
                        <li key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', fontSize: 13, color: 'var(--text-secondary)' }}>
                          <FiPaperclip size={12} style={{ color: 'var(--text-muted)' }} />
                          <span style={{ flex: 1 }}>{a.file.name}</span>
                          <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>{(a.file.size / 1024).toFixed(1)} KB</span>
                          <button type="button" onClick={() => setEmailFormAttachments(prev => prev.filter(x => x.id !== a.id))} style={{ background: 'none', border: 'none', color: 'var(--error-color)', cursor: 'pointer', padding: 4 }} title="Remover">×</button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* AI Agent Selection */}
                <div style={{ marginBottom: 16 }}>
                  <label className="email-broadcast-ai-label" htmlFor="email-broadcast-ai-agent-select">
                    <span className="email-broadcast-ai-label__icon" aria-hidden>
                      <FiCpu size={16} />
                    </span>
                    <span>Funcionário de IA (resposta automática)</span>
                  </label>
                  <select
                    id="email-broadcast-ai-agent-select"
                    value={emailFormAiAgentId ?? ''}
                    onChange={(e) => setEmailFormAiAgentId(e.target.value ? parseInt(e.target.value, 10) : null)}
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--border-color)', borderRadius: 8, fontSize: 14, background: 'var(--bg-input)', color: 'var(--text-primary)' }}
                  >
                    <option value="">-- Sem IA (só manual) --</option>
                    {aiCampaignAgents.map(a => (
                      <option key={a.id} value={a.id}>{a.name}</option>
                    ))}
                  </select>
                  <p style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 4 }}>
                    Quando definido, o funcionário de IA responde automaticamente quando os destinatários responderem a esta campanha.
                  </p>
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Destinatários</label>
                  <div style={{ marginBottom: 12, padding: 12, background: 'var(--bg-elevated)', borderRadius: 8, border: '1px solid var(--border-color)' }}>
                    <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Importar do Excel</div>
                    <p style={{ fontSize: 12, color: 'var(--text-tertiary)', margin: '0 0 8px' }}>
                      Colunas: <strong>Email</strong> (obrigatória), <strong>Name</strong> ou <strong>Nome</strong> (opcional). Use <strong>.xlsx</strong> ou <strong>.xls</strong>.
                    </p>
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      id="email-broadcast-xlsx-input"
                      style={{ display: 'none' }}
                      onChange={handleEmailBroadcastExcel}
                      disabled={emailExcelImporting || !sectorId}
                    />
                    <label
                      htmlFor="email-broadcast-xlsx-input"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '8px 14px',
                        borderRadius: 8,
                        border: '1px solid var(--border-strong)',
                        background: 'var(--bg-card)',
                        color: 'var(--text-primary)',
                        cursor: emailExcelImporting || !sectorId ? 'not-allowed' : 'pointer',
                        fontSize: 13,
                        opacity: emailExcelImporting || !sectorId ? 0.7 : 1,
                      }}
                    >
                      {emailExcelImporting ? 'Importando…' : 'Escolher arquivo do Excel'}
                    </label>
                    {emailFormSelectedContacts.size > 0 && (
                      <span style={{ marginLeft: 10, fontSize: 13, color: 'var(--info-color)', fontWeight: 600 }}>
                        {emailFormSelectedContacts.size} {emailFormSelectedContacts.size === 1 ? 'selecionado' : 'selecionados'}
                      </span>
                    )}
                    {emailExcelError && (
                      <p style={{ color: 'var(--error-color)', fontSize: 12, margin: '8px 0 0' }}>{emailExcelError}</p>
                    )}
                  </div>
                  {emailContactsLoading ? (
                    <div style={{ color: 'var(--text-tertiary)', fontSize: 13 }}>Carregando contatos…</div>
                  ) : emailContacts.length === 0 && emailFormSelectedContacts.size === 0 ? (
                    <div style={{ padding: 16, border: '1px solid var(--border-color)', borderRadius: 8, background: 'var(--bg-card)' }}>
                      <p style={{ color: 'var(--text-tertiary)', fontSize: 13, margin: '0 0 12px' }}>
                        Nenhum contato disponível ainda. Importe um arquivo do Excel acima ou adicione contatos na página Contatos.
                      </p>
                      <button
                        type="button"
                        className="btn-primary"
                        onClick={() => { setShowCreateEmailModal(false); navigate('/contacts?tab=email'); }}
                        style={{ padding: '8px 16px', fontSize: 13 }}
                      >
                        Ir para Contatos
                      </button>
                    </div>
                  ) : emailContacts.length > 0 ? (
                    <select
                      multiple
                      value={Array.from(emailFormSelectedContacts).map(String)}
                      onChange={(e) => {
                        const selected = Array.from(e.target.selectedOptions, (o) => parseInt(o.value, 10));
                        setEmailFormSelectedContacts(new Set(selected));
                      }}
                      style={{
                        width: '100%', minHeight: 160, padding: 8, border: '1px solid var(--border-color)', borderRadius: 8, fontSize: 14,
                        background: 'var(--bg-input)', color: 'var(--text-primary)', cursor: 'pointer',
                      }}
                    >
                      {emailContacts.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name} &lt;{c.email}&gt;
                        </option>
                      ))}
                    </select>
                  ) : null}
                  {emailContacts.length > 0 && (
                    <p style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 6 }}>Segure Ctrl (Windows) ou Cmd (Mac) para mudar a seleção. Para listas grandes, prefira importar do Excel.</p>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
                  <button className="btn-secondary" onClick={() => {
                    setShowCreateEmailModal(false);
                    setEmailFormName(''); setEmailFormSubject(''); setEmailFormBody(''); setEmailFormSelectedContacts(new Set());
                    setEmailFormCc(''); setEmailFormBcc(''); setEmailFormReplyTo(''); setEmailFormAttachments([]); setShowCcBccSection(false); setEmailFormAiAgentId(null); setEmailExcelError(null); setEmailExcelImporting(false);
                  }}>Cancelar</button>
                  <button
                    className="btn-primary"
                    disabled={!emailFormName || !emailFormSubject || !emailFormBody || emailFormSelectedContacts.size === 0 || emailCreating}
                    onClick={async () => {
                      if (!sectorId) return;
                      setEmailCreating(true);
                      try {
                        const attachments: Array<{ fileName: string; contentBase64: string; contentType: string }> = [];
                        for (const a of emailFormAttachments) {
                          const base64 = await new Promise<string>((res, rej) => {
                            const r = new FileReader();
                            r.onload = () => res((r.result as string).split(',')[1] || '');
                            r.onerror = rej;
                            r.readAsDataURL(a.file);
                          });
                          attachments.push({ fileName: a.file.name, contentBase64: base64, contentType: a.file.type || 'application/octet-stream' });
                        }
                        const dto: CreateEmailBroadcastRequest = {
                          sectorId,
                          name: emailFormName,
                          subject: emailFormSubject,
                          bodyHtml: emailFormBody.replace(/\n/g, '<br>'),
                          emailContactIds: Array.from(emailFormSelectedContacts),
                          cc: emailFormCc.trim() || undefined,
                          bcc: emailFormBcc.trim() || undefined,
                          replyTo: emailFormReplyTo.trim() || undefined,
                          attachments: attachments.length > 0 ? attachments : undefined,
                          aiAgentId: emailFormAiAgentId,
                        };
                        await createEmailBroadcast(dto);
                        setShowCreateEmailModal(false);
                        setEmailFormName(''); setEmailFormSubject(''); setEmailFormBody(''); setEmailFormSelectedContacts(new Set());
                        setEmailFormCc(''); setEmailFormBcc(''); setEmailFormReplyTo(''); setEmailFormAttachments([]); setEmailFormAiAgentId(null); setEmailExcelError(null); setEmailExcelImporting(false);
                        loadEmailBroadcasts();
                      } catch (e) { console.error(e); } finally { setEmailCreating(false); }
                    }}
                  >
                    {emailCreating ? 'Criando...' : 'Criar'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Email confirm action modal */}
        <ConfirmModal
          open={emailConfirmAction !== null}
          onClose={() => { setEmailConfirmAction(null); setEmailConfirmBroadcastId(null); }}
          onConfirm={handleEmailConfirmAction}
          title={emailConfirmAction === 'start' ? 'Iniciar campanha' : emailConfirmAction === 'delete' ? 'Excluir campanha' : ''}
          message={emailConfirmAction === 'start' ? 'Iniciar esta campanha de e-mail? Os e-mails serão enviados para todos os destinatários.' : emailConfirmAction === 'delete' ? 'Excluir esta campanha? Esta ação não pode ser desfeita.' : ''}
          confirmLabel={emailConfirmAction === 'delete' ? 'Excluir' : 'Confirmar'}
          cancelLabel="Cancelar"
          variant={emailConfirmAction === 'delete' ? 'danger' : 'default'}
          loading={emailConfirmLoading}
        />
      </div>
    );
  }

  return (
    <div className="broadcast-page">
      <div className="broadcast-header">
        <div className="campaign-header-row">
          <div className="campaign-section">
            <h2 className="campaign-section__title">Campanhas</h2>
            <nav className="campaign-tabs">
              <button className="campaign-tabs__item campaign-tabs__item--active" onClick={() => setActiveTab('campanhas')}>WhatsApp</button>
              {hasModule('email') && <><span className="campaign-tabs__separator" />
              <button className="campaign-tabs__item" onClick={() => setActiveTab('email')}>E-mail</button></>}
            </nav>
          </div>
        </div>
        <div className="broadcast-header-actions">
          {!isOfficial && (
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setShowExportGroupsModal(true)}
              title="Baixar planilha do Excel com os membros dos grupos do WhatsApp selecionados (no formato do modelo de importação)"
            >
              <FiDownload size={14} /> Exportar grupos
            </button>
          )}
          <button className="btn-secondary" onClick={() => loadBroadcasts()}>
            <FiRefreshCw size={14} /> Atualizar
          </button>
          <button className="btn-primary" onClick={handleOpenCreate}>
            <FiPlus size={14} /> Nova campanha
          </button>
        </div>
      </div>

      {renderBroadcastAIBanner()}

      {/* Status Filter Tabs */}
      <div style={{ padding: '0 24px', background: 'var(--bg-card)', borderBottom: '1px solid var(--border-color)' }}>
        <div style={{ display: 'flex', gap: 0, overflowX: 'auto' }}>
          {[
            { value: '', label: 'Todas' },
            { value: 'pending', label: 'Pendentes' },
            { value: 'running', label: 'Em andamento' },
            { value: 'completed', label: 'Concluídas' },
            { value: 'cancelled', label: 'Canceladas' },
          ].map(tab => (
            <button
              key={tab.value}
              onClick={() => handleStatusFilterChange(tab.value)}
              style={{
                padding: '12px 20px',
                border: 'none',
                background: 'transparent',
                color: statusFilter === tab.value ? 'var(--text-primary)' : 'var(--text-muted)',
                fontWeight: statusFilter === tab.value ? 600 : 400,
                fontSize: 14,
                cursor: 'pointer',
                borderBottom: statusFilter === tab.value ? '2px solid var(--primary-color)' : '2px solid transparent',
                transition: 'all 0.2s',
                whiteSpace: 'nowrap',
              }}
            >
              {tab.label}
            </button>
          ))}
          <div style={{ flex: 1 }} />
          <span style={{ padding: '12px 0', fontSize: 13, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
            {totalBroadcasts} {totalBroadcasts === 1 ? 'campanha' : 'campanhas'}
          </span>
        </div>
      </div>

      <div className="broadcast-content">
        {loading ? (
          <div className="broadcast-loading">
            <div className="spinner" />
            Carregando...
          </div>
        ) : broadcasts.length === 0 ? (
          <div className="broadcast-empty">
            <div className="broadcast-empty-icon"><FiRadio size={48} /></div>
            <h3>{statusFilter ? 'Nenhuma campanha neste filtro' : 'Nenhuma campanha ainda'}</h3>
            <p>{statusFilter ? 'Tente outro filtro' : 'Crie sua primeira campanha para enviar mensagens para vários contatos'}</p>
          </div>
        ) : (
          <>
            <div className="broadcast-list">
              {broadcasts.map(b => {
                const progress = getProgressPercent(b);
                return (
                  <div key={b.id} className="broadcast-card" onClick={() => handleViewDetail(b.id)}>
                    <div className="broadcast-card-header">
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span className="broadcast-card-title">{b.name}</span>
                        <span className={`status-badge ${getStatusBadgeClass(b)}`}>{getStatusBadgeLabel(b)}</span>
                      </div>
                      <div className="broadcast-card-actions">
                        {b.status === 'pending' && !isBroadcastAutoPaused(b) && (
                          <button
                            type="button"
                            className="btn-success btn-sm"
                            style={startShortcutArmedBroadcastId === b.id ? { boxShadow: '0 0 0 2px #166534' } : undefined}
                            onClick={(e) => handleBroadcastStartClick(e, b)}
                            onContextMenu={(e) => handlePendingStartButtonContextMenu(e, b.id)}
                            title={
                              startShortcutArmedBroadcastId === b.id
                                ? 'Atalho ativo. O clique roda a verificação de LID (depois você pode Iniciar)'
                                : undefined
                            }
                          >
                            <FiPlay size={12} /> {startShortcutArmedBroadcastId === b.id ? 'Iniciar (atalho)' : 'Iniciar'}
                          </button>
                        )}
                        {canRerunBroadcast(b) && (
                          <button
                            type="button"
                            className="btn-success btn-sm"
                            onClick={(e) => openConfirm('rerun', b.id, e)}
                            title="Reenviar campanha"
                          >
                            <FiRefreshCw size={12} /> Reenviar
                          </button>
                        )}
                        {(isBroadcastAutoPaused(b) || b.status === 'paused' || b.status === 'running') && (
                          <button className="btn-danger btn-sm" onClick={(e) => openConfirm('cancel', b.id, e)} title="Parar campanha">
                            <FiSquare size={12} /> Parar
                          </button>
                        )}
            <button className="btn-icon" onClick={(e) => { e.stopPropagation(); handleOpenEdit(b.id, b); }} title="Editar">
              <FiEdit2 size={14} />
            </button>
            <button className="btn-icon danger" onClick={(e) => openConfirm('delete', b.id, e)} title="Excluir">
                          <FiTrash2 size={14} />
                        </button>
                      </div>
                    </div>
                    <div className="broadcast-card-meta">
                      <span className="broadcast-meta-item">
                        <FiUsers size={13} /> {b.total_count} {b.total_count === 1 ? 'destinatário' : 'destinatários'}
                      </span>
                      <span className="broadcast-meta-item" title="Início da última execução (pelo histórico)">
                        <FiClock size={13} /> Último envio: {getListLastRunLabel(b)}
                      </span>
                      <span className="broadcast-meta-item" style={{ color: 'var(--primary-light)' }} title="Próximo envio">
                        <FiRefreshCw size={13} /> Próximo: {getNextRunLabel(b)}
                      </span>
                      {(b.execution_count != null && b.execution_count > 0) && (
                        <span className="broadcast-meta-item" style={{ color: 'var(--primary-light)' }}>
                          <FiRefreshCw size={13} /> {b.execution_count} {b.execution_count === 1 ? 'execução' : 'execuções'}
                        </span>
                      )}
                      <span className="broadcast-meta-item">
                        <FiCheckCircle size={13} color="var(--success-color)" /> {b.sent_count} {b.sent_count === 1 ? 'enviado' : 'enviados'}
                      </span>
                      {b.failed_count > 0 && (
                        <span className="broadcast-meta-item" style={{ color: 'var(--error-color)' }}>
                          <FiXCircle size={13} /> {b.failed_count} {b.failed_count === 1 ? 'falha' : 'falhas'}
                        </span>
                      )}
                    </div>
                    {(b.status === 'running' || b.status === 'paused' || b.status === 'completed') && b.total_count > 0 && (
                      <div className="broadcast-progress">
                        <div className="broadcast-progress-bar">
                          <div
                            className={`broadcast-progress-fill ${b.status === 'running' || isBroadcastAutoPaused(b) ? 'running' : 'success'}`}
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                        <div className="broadcast-progress-text">
                          <span>{progress}%</span>
                          <span>{b.sent_count + b.failed_count}/{b.total_count}</span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: 8, padding: '20px 0', marginTop: 8,
              }}>
                <button
                  className="btn-secondary btn-sm"
                  disabled={currentPage === 0}
                  onClick={() => handlePageChange(currentPage - 1)}
                  style={{ opacity: currentPage === 0 ? 0.4 : 1 }}
                >
                  ← Anterior
                </button>
                {Array.from({ length: totalPages }, (_, i) => (
                  <button
                    key={i}
                    onClick={() => handlePageChange(i)}
                    style={{
                      padding: '6px 12px',
                      border: currentPage === i ? '1px solid var(--primary-color)' : '1px solid var(--border-color)',
                      borderRadius: 6,
                      background: currentPage === i ? 'var(--primary-color)' : 'var(--bg-card)',
                      color: currentPage === i ? 'var(--text-on-primary)' : 'var(--text-muted)',
                      cursor: 'pointer',
                      fontSize: 13,
                      fontWeight: currentPage === i ? 600 : 400,
                    }}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  className="btn-secondary btn-sm"
                  disabled={currentPage >= totalPages - 1}
                  onClick={() => handlePageChange(currentPage + 1)}
                  style={{ opacity: currentPage >= totalPages - 1 ? 0.4 : 1 }}
                >
                  Próxima →
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="broadcast-modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="broadcast-modal" onClick={(e) => e.stopPropagation()}>
            <div className="broadcast-modal-header">
              <h2>Nova campanha</h2>
              <button className="broadcast-modal-close" onClick={() => setShowCreateModal(false)}>×</button>
            </div>
            <div className="broadcast-modal-body">
              <div className="form-group">
                <label>Nome da campanha</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Ex.: Promoção de janeiro"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Selecionar grupo <span className="required">*</span></label>
                <VirtualizedBroadcastGroupPicker
                  groups={groups}
                  value={selectedGroupId}
                  onChange={(id) => void handleGroupChange(id)}
                  disabled={contactsLoading}
                  requireGroup
                  placeholder="Selecione um grupo do WhatsApp…"
                />
                <div className="form-hint">
                  Escolha um grupo do WhatsApp. Os membros entram como destinatários individuais (mensagens 1:1, não uma postagem no grupo).
                </div>
              </div>

              <div className="form-group broadcast-ai-form-group">
                <div className="broadcast-ai-stack">
                  <label className="broadcast-ai-field-label" htmlFor="broadcast-create-ai-agent">
                    <FiCpu size={16} aria-hidden /> Funcionário de IA
                  </label>
                  <select
                    id="broadcast-create-ai-agent"
                    className="form-select"
                    value={formAiAgentId ?? ''}
                    onChange={(e) => {
                      const v = e.target.value ? Number(e.target.value) : null;
                      setFormAiAgentId(v);
                    }}
                  >
                    <option value="">-- IA da conexão (sem funcionário dedicado) --</option>
                    {aiCampaignAgents.map(a => (
                      <option key={a.id} value={a.id}>{a.name} {a.isEnabled ? '' : '(inativo)'}</option>
                    ))}
                  </select>
                  <p className="broadcast-ai-hint">
                    Opcional, usado só nas respostas. Vazio = IA da conexão com o contexto desta campanha.
                  </p>
                  <label className="broadcast-ai-reply-toggle">
                    <input
                      type="checkbox"
                      checked={formAiReplyEnabled}
                      onChange={(e) => setFormAiReplyEnabled(e.target.checked)}
                    />
                    <span className="broadcast-ai-reply-toggle__copy">
                      <span className="broadcast-ai-reply-toggle__title">A IA responde depois de cada envio</span>
                      <span className="broadcast-ai-reply-toggle__desc">
                        Ligado: cada envio mantém o contato com a IA. Desligado: atendimento humano depois de cada envio.
                      </span>
                    </span>
                  </label>
                </div>
              </div>

              <div className="form-group">
                <label>
                  Destinatários <span className="required">*</span>
                </label>
                <div className="contact-selection">
                  <div className="contact-selection-header">
                    {displayedFormContacts.length > 0 && !contactsLoading && (
                      <label>
                        <input
                          type="checkbox"
                          checked={displayedFormContacts.every(c => formSelectedContacts.has(contactSelectionKey(c)))}
                          onChange={handleSelectAll}
                        />
                        {' '}Selecionar todos
                      </label>
                    )}
                    <span className="contact-selection-count">
                      {displayedFormSelectedCount} {displayedFormSelectedCount === 1 ? 'selecionado' : 'selecionados'}
                      {displayedFormContacts.length > 0 ? ` · ${displayedFormContacts.length} na lista` : ''}
                    </span>
                  </div>
                  <div className="contact-search">
                    <input
                      type="text"
                      placeholder="Buscar contatos..."
                      value={formContactSearch}
                      onChange={(e) => setFormContactSearch(e.target.value)}
                      disabled={contactsLoading}
                    />
                  </div>
                  <div className="contact-selection-list">
                    {contactsLoading ? (
                      <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-tertiary)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                        <LoadingSpinner size="md" />
                        <span>Carregando contatos…</span>
                      </div>
                    ) : !selectedGroupId ? (
                      <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)' }}>Selecione um grupo acima para carregar os membros</div>
                    ) : displayedFormContacts.length === 0 ? (
                      <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)' }}>Nenhum contato encontrado neste grupo</div>
                    ) : (
                      <VirtualizedBroadcastContactList
                        contacts={displayedFormContacts}
                        selectedKeys={formSelectedContacts}
                        getSelectionKey={contactSelectionKey}
                        onToggle={handleToggleContact}
                      />
                    )}
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label><span>Mensagens</span> <span className="required">*</span></label>
                <BroadcastMessageVariantEditor
                  variants={formVariants}
                  onChange={setFormVariants}
                  onOptimizeFirst={handleAiOptimizeFormFirstVariant}
                  aiOptimizing={aiOptimizing === 'message'}
                  onInsertVariable={insertVariableInFormVariant}
                  previewExampleName={PREVIEW_EXAMPLE_NAME}
                />
              </div>

              <div className="form-group" style={{ marginTop: 16 }}>
                <label><span>Recorrência</span></label>
                <select
                  className="form-select"
                  value={recurrenceType}
                  onChange={(e) => setRecurrenceType(Number(e.target.value))}
                >
                  <option value={0}>Uma vez (início manual)</option>
                  <option value={1}>Diária</option>
                  <option value={2}>Semanal</option>
                  <option value={3}>Mensal</option>
                </select>
                <div className="form-hint" style={{ marginTop: 8, marginBottom: 4 }}>
                  Quando cada execução termina, a próxima é agendada automaticamente no seu fuso horário.
                </div>
                <div className="broadcast-schedule">
                    <div className="broadcast-schedule__title">Agendamento</div>
                    <div className="broadcast-schedule__row broadcast-schedule__row--time-date">
                      <div className="broadcast-schedule__field broadcast-schedule__field--time">
                        <span className="broadcast-schedule__label">Horário</span>
                        <div
                          className="broadcast-schedule__picker-trigger"
                          onClick={() => setShowScheduleTimePicker(true)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => e.key === 'Enter' && setShowScheduleTimePicker(true)}
                        >
                          <span className="broadcast-schedule__picker-icon">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
                            </svg>
                          </span>
                          <span className="broadcast-schedule__picker-value">
                            {scheduledTime || '09:00'}
                          </span>
                        </div>
                      </div>
                      <div className="broadcast-schedule__field broadcast-schedule__field--date">
                        <span className="broadcast-schedule__label broadcast-schedule__label--optional">
                          Data final
                          <span className="broadcast-schedule__optional-badge">Opcional</span>
                        </span>
                        <div
                          className="broadcast-schedule__picker-trigger"
                          onClick={() => setShowScheduleDatePicker(true)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => e.key === 'Enter' && setShowScheduleDatePicker(true)}
                        >
                          <span className="broadcast-schedule__picker-icon">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                              <line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" />
                              <line x1="3" y1="10" x2="21" y2="10" />
                            </svg>
                          </span>
                          <span className={`broadcast-schedule__picker-value ${!recurrenceEndDate ? 'broadcast-schedule__picker-value--placeholder' : ''}`}>
                            {recurrenceEndDate ? dayjs(recurrenceEndDate).format('DD/MM/YYYY') : 'dd/mm/aaaa'}
                          </span>
                        </div>
                      </div>
                    </div>
                    {recurrenceType === 2 && (
                      <div className="broadcast-schedule__weekdays-wrap">
                        <span className="broadcast-schedule__weekdays-label">Repetir em <span style={{ color: 'var(--error-color)' }}>(obrigatório)</span></span>
                        <div className="broadcast-schedule__weekdays">
                          {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map((label, i) => (
                            <button
                              key={i}
                              type="button"
                              className={`broadcast-schedule__day ${recurrenceWeekdays.includes(i) ? 'broadcast-schedule__day--selected' : ''}`}
                              onClick={() => setRecurrenceWeekdays(prev => prev.includes(i) ? prev.filter(d => d !== i) : [...prev, i].sort((a, b) => a - b))}
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    {recurrenceType === 3 && (
                      <div className="broadcast-schedule__months-wrap">
                        <span className="broadcast-schedule__months-label">Repetir nos meses <span style={{ color: 'var(--error-color)' }}>(obrigatório)</span></span>
                        <div className="broadcast-schedule__months">
                          {['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'].map((label, i) => (
                            <button
                              key={i}
                              type="button"
                              className={`broadcast-schedule__month ${recurrenceMonths.includes(i + 1) ? 'broadcast-schedule__month--selected' : ''}`}
                              onClick={() => setRecurrenceMonths(prev => prev.includes(i + 1) ? prev.filter(x => x !== i + 1) : [...prev, i + 1].sort((a, b) => a - b))}
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="broadcast-schedule__options">
                      <label className="broadcast-schedule__option">
                        <input type="checkbox" checked={excludeWeekends} onChange={(e) => setExcludeWeekends(e.target.checked)} />
                        Pular fins de semana
                      </label>
                      <label className="broadcast-schedule__option">
                        <input type="checkbox" checked={excludeHolidays} onChange={(e) => setExcludeHolidays(e.target.checked)} />
                        Pular feriados
                      </label>
                    </div>
                    <div className="broadcast-schedule__timezone-row">
                      <span className="broadcast-schedule__label">Fuso horário</span>
                      {timezone ? (
                        <TimezoneSelect
                          value={timezone}
                          onChange={() => {}}
                          disabled
                        />
                      ) : (
                        <div className="broadcast-schedule__tz-warning">
                          <span className="broadcast-schedule__tz-warning-text">
                            Nenhum fuso horário configurado no seu perfil.
                          </span>
                          <button
                            type="button"
                            className="broadcast-schedule__tz-warning-btn"
                            onClick={() => navigate('/profile?tab=settings')}
                          >
                            Ir para Configurações
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
              </div>
            </div>
            {formError && (
              <div style={{
                padding: '10px 16px',
                margin: '0 20px 10px',
                background: 'var(--error-bg)',
                border: '1px solid var(--error-border)',
                borderRadius: 8,
                color: 'var(--error-color)',
                fontSize: 13,
              }}>
                {formError}
              </div>
            )}
            <div className="broadcast-modal-footer">
              <button className="btn-secondary" onClick={() => setShowCreateModal(false)}>
                Cancelar
              </button>
              <button type="button" className="btn-primary" onClick={handleCreate} disabled={creating}>
                {creating ? 'Criando...' : 'Criar campanha'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Time Picker (Create) */}
      {showScheduleTimePicker && (
        <div className="broadcast-datetime-overlay" onClick={() => setShowScheduleTimePicker(false)}>
          <div className="broadcast-datetime-modal" onClick={(e) => e.stopPropagation()}>
            <div className="broadcast-datetime-header">
              <h3 className="broadcast-datetime-title">Selecionar horário</h3>
              <button type="button" className="broadcast-datetime-close" onClick={() => setShowScheduleTimePicker(false)}>×</button>
            </div>
            <div className="broadcast-datetime-content">
              <ScheduleTimePicker
                selectedTime={scheduledTime ? dayjs().hour(parseInt(scheduledTime.slice(0, 2), 10)).minute(parseInt(scheduledTime.slice(3, 5), 10)) : dayjs()}
                onChange={(t) => setScheduledTime(t.format('HH:mm'))}
                timezone={timezone || undefined}
              />
            </div>
            <div className="broadcast-datetime-actions">
              <button type="button" className="broadcast-datetime-cancel" onClick={() => setShowScheduleTimePicker(false)}>Cancelar</button>
              <button type="button" className="broadcast-datetime-confirm" onClick={() => setShowScheduleTimePicker(false)}>Confirmar</button>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Date Picker (Create) */}
      {showScheduleDatePicker && (
        <div className="broadcast-datetime-overlay" onClick={() => setShowScheduleDatePicker(false)}>
          <div className="broadcast-datetime-modal" onClick={(e) => e.stopPropagation()}>
            <div className="broadcast-datetime-header">
              <h3 className="broadcast-datetime-title">Data final (opcional)</h3>
              <button type="button" className="broadcast-datetime-close" onClick={() => setShowScheduleDatePicker(false)}>×</button>
            </div>
            <div className="broadcast-datetime-content">
              <ScheduleCalendar
                selectedDate={recurrenceEndDate ? dayjs(recurrenceEndDate) : null}
                onChange={(d) => {
                  setRecurrenceEndDate(d.format('YYYY-MM-DD'));
                  setShowScheduleDatePicker(false);
                }}
                viewMode="day"
              />
            </div>
            <div className="broadcast-datetime-actions">
              <button type="button" className="broadcast-datetime-cancel" onClick={() => setShowScheduleDatePicker(false)}>Cancelar</button>
              <button type="button" className="broadcast-datetime-confirm" onClick={() => setShowScheduleDatePicker(false)}>Confirmar</button>
            </div>
          </div>
        </div>
      )}

      {renderWhatsAppCampaignOverlays()}

    </div>
  );
};

export default BroadcastPage;
